<template>
  <div class="dir-page eh-ds" data-cy="directives-detail-page">
    <!-- Top utility bar -->
    <div class="dir-utilbar">
      <div class="dir-utilbar-inner">
        <!-- A self-scoped session has no org list to go back to. -->
        <button v-if="!selfScoped" class="dir-back-link" @click="goToList">
          <span class="dir-back-chev">‹</span> Back to patients
        </button>
        <button
          v-if="!accessBlocked && canCreateRecord"
          class="dir-add-btn ghost"
          data-cy="add-directives-btn"
          @click="openAddModal"
        >
          <span class="glyphicon glyphicon-plus bi bi-plus-lg"></span>
          Add New Directives
        </button>
      </div>
    </div>

    <!-- Privilege + scope gate: cannot read, or scope unresolved -->
    <div v-if="accessBlocked" class="dir-empty">
      <span class="glyphicon glyphicon-lock bi bi-lock-fill"></span>
      <h3>{{ blockedMessage }}</h3>
    </div>

    <main v-else class="dir-main">
      <!-- ====== PATIENT IDENTITY BANNER ====== -->
      <section class="dir-banner" data-cy="patient-banner">
        <div class="dir-avatar" :class="{ 'is-loading': !patientIdentityReady }">{{ patientInitials }}</div>
        <div class="dir-banner-body">
          <div class="dir-banner-eyebrow">Patient</div>
          <h1
            class="dir-banner-name"
            :class="{ 'is-loading': !patientIdentityReady }"
            data-cy="patient-name"
          >{{ patientNameDisplay }}</h1>
          <!-- Held back as one block: a half-resolved identity line (an MRN with
               no name, or a DOB from the session user) is worse than none. -->
          <div v-if="patientIdentityReady" class="dir-banner-meta">
            <span v-if="patientDob" class="dir-banner-strong">DOB {{ formatDate(patientDob) }}</span>
            <span v-if="patientAge !== null" class="dir-banner-strong">{{ patientAge }} yrs</span>
            <span v-if="patientMrn || patientId" class="dir-dotsep"></span>
            <span class="dir-banner-mrn">{{ patientMrn || patientId }}</span>
          </div>
          <div v-else class="dir-banner-meta dir-banner-meta-loading">Loading patient details…</div>
        </div>
      </section>

      <!-- Loading -->
      <div v-if="loading" class="dir-loading">
        <div class="dir-loader"></div>
        <p>Loading directives…</p>
      </div>

      <!-- No zero-records empty state: a patient with nothing on file is FULL
           CODE (codeStatus.js), so the card below renders the Full Code hero.
           The utilbar's Add button remains the entry point for new records. -->
      <template v-else>
        <!-- Order is clinical-first: patient identity, then the directives that
             are in force, and only then the document/admin widgets. Someone
             reading this in an emergency should hit the codes before paperwork. -->

        <!-- ====== SUBMISSIONS (one card: in force on top, in flight below) ====== -->
        <!-- Single card for the whole working set. The active record renders
             once, as the hero at the top; the rows below it are only the
             submissions still moving (pending review / unconfirmed
             authorization) — the active record does not repeat as a row, its
             document opens via the View-document button on the hero.
             Taken-care-of submissions (superseded / revoked / expired) live
             in History. -->
        <div class="dir-widget dir-submissions" data-cy="submissions-card">
          <!-- Active hero(s) -->
          <div v-if="hasActive" class="dir-hero-list">
            <section
              v-for="rec in activeRecords"
              :key="rec.id"
              class="dir-hero"
              data-cy="directive-record"
              :data-record-id="rec.id"
              :data-status="rec.status"
            >
              <!-- hero header -->
              <div class="dir-hero-head">
                <div class="dir-hero-head-row">
                  <span class="dir-badge lg active">
                    <span class="dir-badge-dot" :style="{ background: statusDot(rec.status) }"></span>
                    Currently Active
                  </span>
                  <span class="dir-hero-spacer"></span>
                  <!-- Single entry point for everything that is context rather than
                       the directives themselves: provenance, approver, submitter
                       attestation, key dates, audit trail. Labelled rather than
                       icon-only — a bare (i) never said what it opened. -->
                  <button
                    type="button"
                    class="dir-hero-info-btn"
                    data-cy="record-info-btn"
                    title="Record details &amp; actions"
                    @click="openInfo(rec)"
                  >
                    <span class="glyphicon glyphicon-info-sign bi bi-info-circle"></span>
                    <span class="dir-hero-info-text">Details</span>
                  </button>
                </div>
              </div>

              <!-- directives in effect. No subhead: the Active badge above
                   already says what these are. -->
              <div class="dir-hero-body">
                <div v-if="!rec.directives || rec.directives.length === 0" class="dir-pending-box">
                  <template v-if="rec.ingest_path === 'upload'">
                    Document only — no coded directives extracted yet. A reviewer will code this record.
                  </template>
                  <template v-else>No coded directives on this record.</template>
                </div>
                <div v-else class="dir-hero-codes dd-scroll">
                  <div v-for="d in rec.directives" :key="d.id" class="dir-hero-code" :class="codeAccent(d)">
                    <div class="dir-hero-code-icon">{{ codeGlyph(d) }}</div>
                    <div class="dir-hero-code-body">
                      <div class="dir-hero-code-name">{{ enrichDirective(d).name }}</div>
                      <div v-if="enrichDirective(d).description" class="dir-hero-code-desc">
                        {{ enrichDirective(d).description }}
                      </div>
                      <span
                        v-if="d.candidate_status === 'pending_confirmation'"
                        class="dir-pill warn dir-candidate-flag"
                      >
                        <span class="glyphicon glyphicon-exclamation-sign bi bi-exclamation-triangle"></span>
                        Candidate — pending confirmation
                      </span>
                      <span
                        v-if="d.match_confidence != null"
                        class="dir-pill dir-match-confidence"
                        title="OCR match confidence"
                        data-cy="match-confidence"
                      >
                        OCR {{ Math.round(Number(d.match_confidence)) }}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Lapsed re-authorization, stated at the top of the in-force record
                   rather than only in the collapsed detail: this is the prompt for a
                   clinician to act. The record is still ACTIVE and the coded
                   directives above still apply — the wording says so explicitly so
                   "Expired" is never read as "revoked" in an emergency. -->
              <div v-if="reauthOverdue(rec)" class="dir-expired-banner" data-cy="reauth-expired">
                <span class="glyphicon glyphicon-warning-sign bi bi-exclamation-triangle"></span>
                <span>
                  <strong>Re-authorization Expired!</strong>
                  Due {{ formatDate(rec.reauth_due_at) }}. The directives above
                  <strong>remain in force</strong> until an NPI holder re-authorizes them.
                </span>
              </div>

              <!-- Soft clinical-conflict flag (e.g. OCR surfaced Full Code + DNR). -->
              <div v-if="recordConflict(rec)" class="dir-conflict-warning" data-cy="record-conflict">
                <span class="glyphicon glyphicon-warning-sign bi bi-exclamation-triangle"></span>
                {{ conflictMessage }}
              </div>

              <!-- Document affordances as the hero's footer: the codes are what
                   an emergency reader needs first, the source document is the
                   follow-up — so the buttons sit under the list, right above the
                   Submissions section. -->
              <div
                v-if="rec.ingest_path === 'upload' && hasDocument(rec)"
                class="dir-hero-doc-row"
              >
                <button
                  type="button"
                  class="dir-btn-secondary dir-hero-doc-btn"
                  data-cy="view-document"
                  @click="openDocument(rec)"
                >
                  <span class="glyphicon glyphicon-file bi bi-file-earmark-text"></span>
                  View uploaded document
                </button>
                <!-- side-by-side review: codes pane + document (split view) -->
                <button
                  v-if="rec.directives && rec.directives.length"
                  type="button"
                  class="dir-doc-link dir-hero-alongside"
                  data-cy="view-alongside"
                  @click="openAssigner(rec, 'review')"
                >View codes alongside →</button>
              </div>

              <!-- Nothing else on the hero. Approver, NPI, re-auth pill, submitter
                   attestation, audit trail and every action live behind the (i).
                   An emergency caregiver reads status and directives, nothing
                   administrative. -->
            </section>
          </div>

          <!-- FULL CODE hero: a computed UI state, not a record. THE RULE
               (codeStatus.js): a patient is FULL CODE unless an NPI holder has
               validated a directive record — zero records, pending-only and
               history-only all land here, matching the list page's green badge.
               Same hero UI as an active record, but no action buttons: there is
               no record to open details on, no document to view. The muted
               reason line keeps the presumption honest for reader and auditor. -->
          <section v-else class="dir-hero" data-cy="fullcode-hero">
            <div class="dir-hero-head">
              <div class="dir-hero-head-row">
                <span class="dir-badge lg active">
                  <span class="dir-badge-dot" :style="{ background: statusDot('active') }"></span>
                  Currently Active
                </span>
              </div>
            </div>
            <div class="dir-hero-body">
              <div class="dir-hero-codes">
                <div class="dir-hero-code affirming">
                  <div class="dir-hero-code-icon">FC</div>
                  <div class="dir-hero-code-body">
                    <div class="dir-hero-code-name">Full Code</div>
                    <div class="dir-hero-code-desc">
                      Attempt all resuscitative measures, including CPR and intubation.
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="fullCodeBecause" class="dir-fullcode-because" data-cy="fullcode-because">{{ fullCodeBecause }}</div>
            </div>
          </section>

          <!-- Section title sits between the active hero and the in-flight
               rows: it labels the submissions, not the hero above it. -->
          <div class="dir-widget-label-row">
            <div class="dir-widget-label">Submissions</div>
            <!-- The status legend explains the badges on this card and
                 throughout the page. -->
            <button
              class="dir-legend-q"
              data-cy="legend-btn"
              title="What do the statuses mean?"
              aria-label="What do the statuses mean?"
              @click="toggleLegend"
            >?</button>
          </div>

          <div v-if="inFlightRecords.length">
            <div
              v-for="rec in inFlightRecords"
              :key="rec.id"
              class="dir-submission-row"
              data-cy="submission-row"
              :data-record-id="rec.id"
              :data-status="rec.status"
            >
              <!-- Status leads the row on its own line — it is the first thing
                 a reader needs. The badge doubles as an entry point to the
                 record-details modal (same target as "More actions"). -->
              <button
                type="button"
                class="dir-badge dir-badge-btn dir-submission-status"
                :class="badgeClass(rec.status)"
                data-cy="latest-form-status"
                title="Record details &amp; actions"
                @click="openInfo(rec)"
              >
                <span class="dir-badge-dot" :style="{ background: statusDot(rec.status) }"></span>
                {{ statusLabels[rec.status] }}
              </button>
              <div class="dir-submission-band">
                <div class="dir-file-chip">
                  <span
                    v-if="rec.ingest_path === 'manual'"
                    class="glyphicon glyphicon-pencil bi bi-pen"
                    aria-hidden="true"
                  ></span>
                  <template v-else>{{ fileExt(rec) }}</template>
                </div>
                <div class="dir-widget-form-body dir-submission-main">
                  <span class="dir-widget-filename">{{ submissionName(rec) }}</span>
                  <div class="dir-widget-sub">
                    {{ rec.ingest_path === "manual" ? "Submitted" : "Uploaded" }}
                    {{ formatDate(rec.created_at) }}
                  </div>
                  <!-- Same document affordances as the active hero, scoped to
                       this submission. Plain "View document": the button sits
                       inside the row it opens (file chip + status badge right
                       there), and the active hero's button already carries the
                       "active" qualifier — "new" would go stale on a record
                       that lingers unconfirmed. -->
                  <div v-if="hasDocument(rec)" class="dir-submission-doc-row">
                    <button
                      type="button"
                      class="dir-btn-secondary dir-submission-doc-btn"
                      data-cy="latest-form-link"
                      @click="openDocument(rec)"
                    >
                      <span class="glyphicon glyphicon-file bi bi-file-earmark-text"></span>
                      View document
                    </button>
                    <button
                      v-if="rec.directives && rec.directives.length"
                      type="button"
                      class="dir-doc-link"
                      data-cy="row-view-alongside"
                      @click="openAssigner(rec, 'review')"
                    >View codes alongside →</button>
                    <button
                      v-if="canRemoveDocument(rec)"
                      type="button"
                      class="dir-doc-link dir-doc-remove"
                      data-cy="remove-document"
                      @click="openRemoveDocument(rec)"
                    >Remove document</button>
                  </div>
                  <div
                    v-else-if="directiveDocumentState(rec) === 'unavailable'"
                    class="dir-submission-doc-row"
                  >
                    <span class="dir-doc-state" data-cy="document-unavailable">{{ documentUnavailableText }}</span>
                  </div>
                </div>
                <div class="dir-widget-actions dir-submission-actions">
                  <!-- When the needed action is someone else's, say whose turn
                       it is instead of leaving unexplained empty space. -->
                  <span v-if="rowWaitingText(rec)" class="dir-row-waiting" data-cy="row-waiting">
                    {{ rowWaitingText(rec) }}
                  </span>
                  <!-- Prominent CTAs on the in-flight row. Direct sign-off only
                       for users who clear both sign gates (privilege + NPI);
                       everyone else goes through "More actions", where the next
                       step is spelled out. -->
                  <button
                    v-if="rowSignable(rec)"
                    class="dir-btn-primary"
                    data-cy="latest-form-sign"
                    @click="onSign(rec)"
                  >
                    <span class="glyphicon glyphicon-pencil bi bi-pen"></span>
                    Sign &amp; Activate
                  </button>
                  <button
                    v-if="rowConfirmable(rec)"
                    class="dir-btn-primary"
                    data-cy="latest-form-confirm"
                    @click="onConfirmAuthorization(rec)"
                  >
                    <span class="glyphicon glyphicon-ok-circle bi bi-shield-check"></span>
                    Confirm Authorization
                  </button>
                  <button
                    class="dir-btn-secondary"
                    data-cy="latest-form-actions"
                    @click="openInfo(rec)"
                  >
                    More actions
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="dir-widget-empty">No pending submissions.</div>
        </div>

        <!-- ====== HISTORY (taken care of: superseded / revoked / expired) ====== -->
        <div v-if="otherRecords.length" class="dir-other">
          <div class="dir-other-head" data-cy="other-toggle" @click="toggleOther">
            <span class="dir-other-title">History</span>
            <span class="dir-other-count">{{ otherRecords.length }}</span>
            <span class="dir-other-spacer"></span>
            <span class="dir-other-summary">{{ otherSummary }}</span>
            <span class="dir-other-chev" :class="{ open: otherOpen }">⌄</span>
          </div>

          <transition name="dir-slide">
            <div v-show="otherOpen" class="dir-other-body">
              <div
                v-for="rec in otherRecords"
                :key="rec.id"
                class="dir-orec"
                data-cy="directive-record"
                :data-record-id="rec.id"
                :data-status="rec.status"
              >
                <div class="dir-orec-row" data-cy="record-row" @click="toggleExpanded(rec.id)">
                  <span class="dir-badge" :class="badgeClass(rec.status)">
                    <span class="dir-badge-dot" :style="{ background: statusDot(rec.status) }"></span>
                    {{ statusLabels[rec.status] }}
                  </span>
                  <div class="dir-orec-main">
                    <div class="dir-orec-form">{{ provenanceText(rec) }}</div>
                    <!-- The provenance is already the line above; repeating it
                         here left manual records reading "Manual entry" twice. -->
                    <div class="dir-orec-sub">{{ summarizeDirectives(rec) }}</div>
                  </div>
                  <div class="dir-orec-date">
                    <small>{{ approvalCaption(rec) }}</small>
                    <strong>{{ approvalText(rec) }}</strong>
                  </div>
                  <span class="dir-orec-chev" :class="{ open: isExpanded(rec.id) }">⌄</span>
                </div>

                <transition name="dir-slide">
                  <div v-show="isExpanded(rec.id)" class="dir-orec-detail">
                    <!-- The record stays ACTIVE and its directives stay in force.
                         The second sentence is load-bearing: "expired" must not be
                         read by clinical staff as "no longer applies". -->
                    <div v-if="reauthOverdue(rec)" class="dir-soft-warning">
                      <span class="glyphicon glyphicon-warning-sign bi bi-exclamation-triangle"></span>
                      <strong>Re-authorization Expired!</strong>
                      It was due {{ formatDate(rec.reauth_due_at) }}.
                      These directives remain in force until re-authorized by an NPI holder.
                    </div>
                    <div v-else-if="reauthDueSoon(rec)" class="dir-soft-warning info">
                      <span class="glyphicon glyphicon-time bi bi-clock-history"></span>
                      Re-authorization due {{ formatDate(rec.reauth_due_at) }} ({{ reauthDaysLeft(rec) }} days).
                    </div>

                    <div v-if="!rec.directives || rec.directives.length === 0" class="dir-pending-box">
                      <template v-if="rec.ingest_path === 'upload'">
                        Document only — no coded directives extracted yet. A reviewer will code this record before it can be activated.
                      </template>
                      <template v-else>No coded directives on this record.</template>
                    </div>
                    <div v-else class="dir-code-cards">
                      <div v-for="d in rec.directives" :key="d.id" class="dir-code-card">
                        <div class="dir-code-card-body">
                          <div class="dir-code-name">{{ enrichDirective(d).name }}</div>
                          <div v-if="enrichDirective(d).description" class="dir-code-desc">{{ enrichDirective(d).description }}</div>
                          <span v-if="d.candidate_status === 'pending_confirmation'" class="dir-pill warn dir-candidate-flag">
                            <span class="glyphicon glyphicon-exclamation-sign bi bi-exclamation-triangle"></span>
                            Candidate — pending confirmation
                          </span>
                          <span v-if="d.match_confidence != null" class="dir-pill dir-match-confidence" title="OCR match confidence" data-cy="match-confidence">
                            OCR {{ Math.round(Number(d.match_confidence)) }}%
                          </span>
                        </div>
                      </div>
                    </div>

                    <div v-if="recordConflict(rec)" class="dir-conflict-warning" data-cy="record-conflict">
                      <span class="glyphicon glyphicon-warning-sign bi bi-exclamation-triangle"></span>
                      {{ conflictMessage }}
                    </div>

                    <!-- Same treatment as the active card: attestation, dates,
                         document links, audit trail and every action live behind
                         the (i), not inline. -->
                    <button
                      type="button"
                      class="dir-orec-info"
                      data-cy="record-info-btn"
                      @click.stop="openInfo(rec)"
                    >
                      <span class="glyphicon glyphicon-info-sign bi bi-info-circle"></span>
                      Record details &amp; actions
                    </button>
                  </div>
                </transition>
              </div>
            </div>
          </transition>
        </div>
      </template>
    </main>

    <!-- Record info modal — everything that is context rather than the
         directives themselves. Reached only from the (i) on the record card. -->
    <Teleport to="body">
      <div v-if="infoRec" class="dir-modal-overlay eh-ds" data-cy="record-info-modal" @click.self="closeInfo">
        <div class="dir-modal dir-info-modal">
          <div class="dir-modal-header">
            <div>
              <h3>
                Record details
                <span class="dir-badge sm" :class="badgeClass(infoRec.status)">
                  <span class="dir-badge-dot" :style="{ background: statusDot(infoRec.status) }"></span>
                  {{ statusLabels[infoRec.status] }}
                </span>
              </h3>
              <div class="dir-modal-patient" data-cy="modal-patient">{{ patientIdentityLine }}</div>
            </div>
            <button class="dir-close-btn" @click="closeInfo" aria-label="Close">
              <span class="glyphicon glyphicon-remove bi bi-x-lg"></span>
            </button>
          </div>
          <div class="dir-modal-body">
            <!-- ===== Next step first: the record's lifecycle position + this
                 user's capabilities compressed into one sentence, so the
                 action rows below read as options, not a puzzle. When it is
                 someone else's turn, this line says whose. ===== -->
            <div v-if="nextStepText(infoRec)" class="dir-next-step" data-cy="next-step">
              <span class="glyphicon glyphicon-arrow-right bi bi-arrow-right-circle"></span>
              <span>{{ nextStepText(infoRec) }}</span>
            </div>

            <!-- Open review flag — the visible state that makes "Review
                 Needed" / "Mark Reviewed" a coherent pair instead of two
                 unrelated stamps. Resolving it is Mark Reviewed below. -->
            <div v-if="openReviewFlag(infoRec)" class="dir-review-flag" data-cy="open-review-flag">
              <span class="glyphicon glyphicon-flag bi bi-flag"></span>
              <span>
                <strong>Flagged for review</strong> by {{ openReviewFlag(infoRec).actor_name }}
                · {{ formatDateTime(openReviewFlag(infoRec).occurred_at) }}<template
                  v-if="openReviewFlag(infoRec).note"
                > — “{{ openReviewFlag(infoRec).note }}”</template>
              </span>
            </div>

            <!-- ===== Actions, grouped by ACTOR: the tester's "who reviews?"
                 confusion is answered structurally. Buttons the user can never
                 use (missing privilege / NPI) are absent, not disabled — a
                 disabled button reads as "locked until I do something". ===== -->
            <div v-if="clinicianActions.length" class="dir-action-group">
              <div class="dir-action-group-label">Clinician actions</div>
              <div class="dir-actions dir-info-actions">
                <button
                  v-if="clinicianActions.includes('sign')"
                  class="dir-btn-primary"
                  data-cy="action-sign"
                  @click="onSign(infoRec)"
                >
                  <span class="glyphicon glyphicon-pencil bi bi-pen"></span>
                  Sign &amp; Activate
                </button>
                <button
                  v-if="clinicianActions.includes('reauthorize')"
                  class="dir-btn-secondary"
                  :class="{ 'dir-btn-warning': reauthOverdue(infoRec) }"
                  data-cy="action-reauthorize"
                  @click="onReauthorize(infoRec)"
                >
                  <span class="glyphicon glyphicon-refresh bi bi-arrow-repeat"></span>
                  Re-authorize
                </button>
                <button
                  v-if="clinicianActions.includes('reviewed')"
                  :class="openReviewFlag(infoRec) ? 'dir-btn-primary' : 'dir-btn-secondary'"
                  data-cy="action-mark-reviewed"
                  @click="onReviewed(infoRec)"
                >
                  <span class="glyphicon glyphicon-ok bi bi-check2"></span>
                  {{ openReviewFlag(infoRec) ? "Resolve — mark reviewed" : "Mark Reviewed" }}
                </button>
                <button
                  v-if="clinicianActions.includes('review-needed')"
                  class="dir-btn-warning"
                  data-cy="action-review-needed"
                  @click="openReviewNeeded(infoRec)"
                >
                  <span class="glyphicon glyphicon-flag bi bi-flag"></span>
                  {{ openReviewFlag(infoRec) ? "Edit flag" : "Review Needed" }}
                </button>
              </div>
              <small
                v-if="lastReviewedEvent(infoRec) && !openReviewFlag(infoRec)"
                class="dir-last-reviewed"
                data-cy="last-reviewed"
              >
                Last reviewed by {{ lastReviewedEvent(infoRec).actor_name }}
                · {{ formatDateTime(lastReviewedEvent(infoRec).occurred_at) }}
              </small>
            </div>

            <div v-if="submissionActions.length" class="dir-action-group">
              <div class="dir-action-group-label">Submission actions</div>
              <div class="dir-actions dir-info-actions">
                <button
                  v-if="submissionActions.includes('confirm')"
                  class="dir-btn-primary"
                  data-cy="action-confirm-authorization"
                  @click="onConfirmAuthorization(infoRec)"
                >
                  <span class="glyphicon glyphicon-ok-circle bi bi-shield-check"></span>
                  Confirm Authorization
                </button>
                <button
                  v-if="submissionActions.includes('add-directives')"
                  class="dir-btn-secondary"
                  data-cy="action-add-directives"
                  @click="onAddDirectives(infoRec)"
                >
                  <span class="glyphicon glyphicon-plus bi bi-plus-lg"></span>
                  {{ infoRec.directives && infoRec.directives.length ? 'Add Directives' : 'Add Directives from Document' }}
                </button>
                <button
                  v-if="submissionActions.includes('delete')"
                  class="dir-btn-danger"
                  data-cy="action-delete"
                  @click="onDelete(infoRec)"
                >
                  <span class="glyphicon glyphicon-trash bi bi-trash"></span>
                  Delete current submission
                </button>
              </div>
            </div>

            <div v-if="reviewNeededFor === infoRec.id" class="dir-note-box" data-cy="review-needed-box">
              <label>Why does this need review?</label>
              <textarea v-model.trim="reviewNote" class="dir-input" rows="2" placeholder="Short note (required)…" data-cy="review-note"></textarea>
              <div class="dir-note-actions">
                <button class="dir-btn-secondary" @click="cancelReviewNeeded">Cancel</button>
                <button class="dir-btn-warning" data-cy="review-note-submit" :disabled="!reviewNote" @click="submitReviewNeeded(infoRec)">Save</button>
              </div>
            </div>

            <div v-if="documentAreaFor(infoRec)" class="dir-orec-doclinks dir-info-doclinks">
              <button
                v-if="hasDocument(infoRec)"
                type="button"
                class="dir-doc-link"
                data-cy="view-document"
                @click="openDocument(infoRec)"
              >View document →</button>
              <button
                v-if="hasDocument(infoRec) && infoRec.directives && infoRec.directives.length"
                type="button"
                class="dir-doc-link"
                data-cy="view-alongside"
                @click="openAssigner(infoRec, 'review')"
              >View alongside codes →</button>
              <button
                v-if="hasDocument(infoRec) && canRemoveDocument(infoRec)"
                type="button"
                class="dir-doc-link dir-doc-remove"
                data-cy="remove-document"
                @click="openRemoveDocument(infoRec)"
              >Remove document</button>
              <span
                v-if="directiveDocumentState(infoRec) === 'unavailable'"
                class="dir-doc-state"
                data-cy="document-unavailable"
              >{{ documentUnavailableText }}</span>
            </div>

            <div v-if="proxyNotice(infoRec)" class="dir-soft-warning sm">
              <span class="glyphicon glyphicon-warning-sign bi bi-exclamation-triangle"></span>
              Health-care-proxy directives must be witnessed / notarized to be activated.
            </div>

            <div class="dir-info-approver">
              <div class="dir-approver-avatar">{{ signerInitials(infoRec) }}</div>
              <div class="dir-approver-text">
                <div class="dir-approver-line">
                  <strong>Approved by {{ infoRec.approved_by_name || '—' }}</strong>
                  <span class="dir-muted-inline"> · {{ approvalText(infoRec) }}</span>
                </div>
                <div v-if="infoRec.approved_by_npi" class="dir-approver-npi">NPI {{ infoRec.approved_by_npi }}</div>
              </div>
            </div>

            <div v-if="reauthPill(infoRec)" class="dir-reauth-pill" :class="reauthPill(infoRec).cls">
              <span class="dir-reauth-icon">{{ reauthPill(infoRec).icon }}</span>
              {{ reauthPill(infoRec).text }}
            </div>

            <div class="dir-detail-grid">
              <div class="dir-detail-col">
                <h4>Submitter &amp; attestation</h4>
                <div class="dir-kv">
                  <div class="dir-kv-row"><span>Source</span><strong>{{ provenanceText(infoRec) }}</strong></div>
                  <div class="dir-kv-row"><span>Submitted by</span><strong>{{ infoRec.created_by_name }}</strong></div>
                  <div class="dir-kv-row"><span>Relationship</span><strong>{{ relationshipLabel(infoRec) }}</strong></div>
                  <div class="dir-kv-row"><span>Recognized surrogate</span><strong>{{ infoRec.is_legal_surrogate ? 'Yes' : 'No' }}</strong></div>
                  <div v-if="infoRec.is_legal_surrogate && infoRec.authority_type" class="dir-kv-row">
                    <span>Authority</span><strong>{{ authorityLabels[infoRec.authority_type] }}</strong>
                  </div>
                </div>
              </div>
              <div class="dir-detail-col">
                <h4>Key dates</h4>
                <div class="dir-kv">
                  <div class="dir-kv-row"><span>Created</span><strong>{{ formatDate(infoRec.created_at) }}</strong></div>
                  <div class="dir-kv-row"><span>Approved</span><strong>{{ approvalText(infoRec) }}</strong></div>
                  <div v-if="infoRec.last_reviewed_at" class="dir-kv-row"><span>Last reviewed</span><strong>{{ formatDate(infoRec.last_reviewed_at) }}</strong></div>
                  <div v-if="infoRec.reauth_due_at" class="dir-kv-row"><span>Re-auth due</span><strong>{{ formatDate(infoRec.reauth_due_at) }}</strong></div>
                  <!-- Which state's rule set that deadline, and how long it is.
                       Shown so a clinician can see WHY this record is on a
                       60-day cadence when another is on 90. -->
                  <div v-if="infoRec.governing_state" class="dir-kv-row">
                    <span>Governed by</span>
                    <strong>{{ governingRuleText(infoRec) }}</strong>
                  </div>
                </div>
              </div>
            </div>

            <h4>Audit trail</h4>
            <div class="dir-timeline">
              <div v-for="ev in sortedEvents(infoRec)" :key="ev.id" class="dir-tl-item">
                <div class="dir-tl-rail">
                  <span class="dir-tl-dot" :style="{ background: statusDot(eventStatusHint(ev)) }"></span>
                  <span class="dir-tl-line"></span>
                </div>
                <div class="dir-tl-body">
                  <div class="dir-tl-head">
                    <span class="dir-tl-action">{{ eventLabels[ev.event_type] || ev.event_type }}</span>
                    <span class="dir-tl-when">· {{ formatDateTime(ev.occurred_at) }}</span>
                  </div>
                  <div class="dir-tl-who">
                    {{ ev.actor_name }}<template v-if="ev.actor_npi"> · NPI {{ ev.actor_npi }}</template>
                  </div>
                  <div v-if="ev.note" class="dir-tl-note">“{{ ev.note }}”</div>
                </div>
              </div>
            </div>
          </div>
          <div class="dir-modal-footer">
            <button class="dir-btn-primary" @click="closeInfo">Close</button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Status legend modal -->
    <Teleport to="body">
      <div v-if="legendOpen" class="dir-modal-overlay eh-ds" data-cy="legend-modal" @click.self="toggleLegend">
        <div class="dir-modal dir-legend-modal">
          <div class="dir-modal-header">
            <h3>What the statuses mean</h3>
            <button class="dir-close-btn" @click="toggleLegend" aria-label="Close">
              <span class="glyphicon glyphicon-remove bi bi-x-lg"></span>
            </button>
          </div>
          <div class="dir-modal-body">
            <div v-for="s in legendStatuses" :key="s" class="dir-legend-item">
              <span class="dir-badge sm" :class="badgeClass(s)">
                <span class="dir-badge-dot" :style="{ background: statusDot(s) }"></span>
                {{ statusLabels[s] }}
              </span>
              <span class="dir-legend-desc">{{ statusDescriptions[s] }}</span>
            </div>
          </div>
          <div class="dir-modal-footer">
            <button class="dir-btn-primary" @click="toggleLegend">Close</button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Add modal -->
    <AddDirectivesModal
      :show="showAddModal"
      :default-relationship="defaultRelationship"
      :patient-name="patientNameDisplay"
      :patient-dob="patientDob"
      :patient-line="patientIdentityLine"
      :submitter-is-patient="isSelf"
      :saving="saving"
      @cancel="showAddModal = false"
      @submit="onAddSubmit"
    />

    <!-- Remove a pending submission's uploaded document, with a reason. -->
    <RemoveFileDialog
      :show="!!removeDocumentRec"
      :file-name="removeDocumentRec ? documentName(removeDocumentRec) : ''"
      :busy="removeDocumentBusy"
      :error-text="removeDocumentError"
      :note="removeDocumentNote"
      @confirm="confirmRemoveDocument"
      @cancel="closeRemoveDocument"
    />

    <!-- Full-screen assign/add/review split view: codes pane + document viewer
         side by side, so nobody codes a record blind. -->
    <DirectiveCodeAssigner
      :show="showAssignModal"
      :mode="assignerMode"
      :saving="saving"
      :patient-name="patientNameDisplay"
      :patient-line="patientIdentityLine"
      :record-label="assignerRecordLabel"
      :doc-url="assignerDocUrl"
      :doc-title="assignerDocTitle"
      :doc-content-type="assignerDocContentType"
      :review-directives="assignerReviewDirectives"
      :approver-line="assignerApproverLine"
      @cancel="closeAssigner"
      @confirm="onAssignConfirm"
    />

    <!-- Inline document viewer (same route) -->
    <DirectiveDocumentViewer
      :show="showDocViewer"
      :url="docViewerUrl"
      :content-type="docViewerContentType"
      :title="docViewerTitle"
      :patient-line="patientIdentityLine"
      :can-add-directives="canAddDirectivesTo(docViewerRec)"
      @close="showDocViewer = false"
      @add-directives="onAddDirectives(docViewerRec)"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { usePrivilegesStore } from "@/stores/privileges";
import { PRIVILEGES } from "@/constants/privileges";
import {
  directiveDocumentExt,
  directiveDocumentName,
  directiveDocumentState,
  directiveDocumentUrl,
  directiveViewerFor,
  hasDocument,
} from "@/utils/directiveDocument";
import AddDirectivesModal from "@/components/private-layouts/directives/AddDirectivesModal.vue";
import DirectiveCodeAssigner from "@/components/private-layouts/directives/DirectiveCodeAssigner.vue";
import DirectiveDocumentViewer from "@/components/private-layouts/directives/DirectiveDocumentViewer.vue";
import { hasConflict, CONFLICT_MESSAGE } from "@/utils/directiveConflicts";
import { ageInYears, formatDate as formatDateValue } from "@/utils/dateOnly";
import {
  getDirectiveAccountNumber,
  getOwnPatientPid,
  getSessionPatientPersonalId,
  getSessionUserId,
  isViewingPatientChart,
} from "@/utils/directivesSession";
import { useSessionStore } from "@/stores/session";
import {
  resolveDirectiveScope,
  canUseSessionAsPatientIdentity,
  directiveGateInputsReady,
  shouldRetryDirectiveInit,
  SCOPE,
} from "@/utils/directivesScope";
import {
  STATUS_LABELS,
  STATUS_DOTS,
  STATUS_DESCRIPTIONS,
  RELATIONSHIP_LABELS,
  AUTHORITY_LABELS,
  AUTHORITY_TYPE,
  EVENT_LABELS,
  DIRECTIVE_STATUS,
  sortDirectiveRecords,
  codeKey,
  getDirectiveRecordsMock,
  getDirectiveCatalogMock,
  getDirectiveRecords,
  createManualDirectiveRecord,
  createUploadDirectiveRecord,
  signDirectiveRecord,
  addDirectivesToRecord,
  markDirectiveReviewed,
  flagDirectiveReviewNeeded,
  reauthorizeDirectiveRecord,
  revokeDirectiveRecord,
  resolveDirectivePatientPid,
  getDirectiveSignerCredential,
  confirmDirectiveAuthorization,
} from "@/api/directivesApi";
import { stateNameForCode } from "@/constants/US-states";
import { resolveKeyonPrivilegeIdentity } from "@/api/privilegesApi";
import { useSessionSelfStore } from "@/stores/sessionSelf";
import { sessionActorFrom } from "@/utils/sessionIdentity";
import EventBus from "@/event-bus";
import { DIRECTIVES_CHANGED } from "@/constants/directive-events";
import { refusalMessage } from "@/api/refusals";
import RemoveFileDialog from "@/components/includes/RemoveFileDialog.vue";
import {
  DIRECTIVE_DOCUMENT_REMOVE_NOTE,
  DIRECTIVE_DOCUMENT_REMOVED_TEXT,
  canDeleteDirectiveDocument,
} from "@/utils/fileDelete";
import { deleteDirectiveDocument } from "@/api/keyonFiles";
import { DOCUMENT_UNAVAILABLE_TEXT } from "@/utils/fileUrl";

// Catalog enrichment for code descriptions still uses the seeded catalog mock,
// which matches the live catalog 1:1.
const USE_MOCK_DATA = false;

export default {
  name: "DirectivesDetailPage",
  components: {
    AddDirectivesModal, DirectiveCodeAssigner, DirectiveDocumentViewer, RemoveFileDialog,
  },
  data() {
    return {
      privilegesStore: usePrivilegesStore(),
      records: [],
      // Identity of the patient whose records these are (name/MRN/DOB), returned
      // by GetDirectiveRecords. The session only carries the logged-in user, so
      // the detail header needs this to show the correct patient.
      targetPatient: null,
      // NPI / professional license keyon holds for the logged-in user. The
      // session payload only carries pacmny-be's Postgres copy, and a clinician
      // onboarded through Add User / verify-provider has theirs in keyon
      // instead — so the sign gate has to consult both. "" = keyon holds none.
      signerCredential: "",
      loading: true,
      // Inverted from the old single `expandedId`: history rows are expanded by
      // DEFAULT and this tracks the ones the user has explicitly collapsed. Two
      // reasons — the details (codes in force, "Record details & actions") were
      // costing a click each to reach, and tracking collapsed-instead-of-expanded
      // means records loaded later are open too, without re-seeding anything.
      collapsedIds: [],
      showAddModal: false,
      showAssignModal: false,
      assignCodesFor: null,
      // 'assign' (code + sign a document-only record) | 'review' (read-only
      // codes alongside the source document).
      assignerMode: "assign",
      showDocViewer: false,
      docViewerUrl: "",
      docViewerContentType: "",
      docViewerTitle: "",
      docViewerRecId: null,
      // Removing a pending submission's document (keyon DeleteDirectiveDocument).
      removeDocumentRec: null,
      removeDocumentBusy: false,
      removeDocumentError: "",
      removeDocumentNote: DIRECTIVE_DOCUMENT_REMOVE_NOTE,
      documentUnavailableText: DOCUMENT_UNAVAILABLE_TEXT,
      saving: false,
      reviewNeededFor: null,
      reviewNote: "",
      legendOpen: false,
      otherOpen: false,
      // Id of the record whose "record details" modal is open (null = none).
      // Everything that is not the directives themselves lives in there —
      // provenance, approver, attestation, key dates, audit trail AND every
      // action. In an emergency the card must not make a caregiver read past
      // administrative controls to find the code status.
      infoRecId: null,
      statusLabels: STATUS_LABELS,
      statusDescriptions: STATUS_DESCRIPTIONS,
      relationshipLabels: RELATIONSHIP_LABELS,
      authorityLabels: AUTHORITY_LABELS,
      eventLabels: EVENT_LABELS,
      legendStatuses: [
        DIRECTIVE_STATUS.ACTIVE,
        DIRECTIVE_STATUS.PENDING_REVIEW,
        DIRECTIVE_STATUS.UNCONFIRMED_AUTHORIZATION,
        DIRECTIVE_STATUS.EXPIRED,
        DIRECTIVE_STATUS.SUPERSEDED,
        DIRECTIVE_STATUS.REVOKED,
      ],
      // MySQL patients.pid for a self-scoped session (see directivesSession.js).
      // Populated async via GetCurrentPatientData; drives patientId + API calls.
      resolvedPatientPid: "",
      // Guards the created()/watcher pair so the chart loads exactly once.
      // `initialized` means SETTLED, not "has run": a pass that failed closed
      // because the session had not arrived leaves it false, so the watcher
      // can try again once it has.
      initialized: false,
      initializing: false,
      // Resolved read scope for the acting subject (see utils/directivesScope.js).
      // Null until created() finishes; treated as blocked until then.
      scope: null,
    };
  },
  computed: {
    ...mapGetters("login", { allData: "getAllData" }),
    patient() {
      return this.allData?.patient || {};
    },
    manager() {
      return this.allData?.manager || {};
    },
    viewingPatientChart() {
      return isViewingPatientChart(this.allData);
    },
    /**
     * Sees exactly one record — its own. True for a patient/family/contact
     * login as well as for a staff member inside a chart. Keying this off
     * the open-chart flag alone is what let a directly-logged-in patient read
     * any pid by typing it into the URL.
     */
    selfScoped() {
      return this.viewingPatientChart || (this.scope && this.scope.kind === SCOPE.SELF);
    },
    /** Fail closed: no privilege, or a scope we could not resolve. */
    accessBlocked() {
      if (!this.canRead) return true;
      return !this.scope || this.scope.kind === SCOPE.NONE;
    },
    blockedMessage() {
      if (!this.canRead) return "You do not have permission to view directives.";
      if (this.scope && this.scope.reason === "not_assigned") {
        return "This patient is not assigned to you.";
      }
      return "We could not confirm which records you may view.";
    },
    // Route param is patient_pid from GetDirectivePatients (org flow). A
    // self-scoped session ignores the route entirely and locks to
    // resolvedPatientPid — session externId is psersonalID, not patient_pid.
    patientId() {
      if (this.selfScoped && this.resolvedPatientPid) {
        return this.resolvedPatientPid;
      }
      return this.$route.params.patientId || "";
    },
    // Patient Information widget fields (session field names confirmed in
    // UserProfile.vue: firstName/lastName/dob; externId/externId2 are the ids).
    // The session's own `patient` block is the LOGGED-IN user, who is only the
    // subject of this page when the session is self-scoped. Falling back to it
    // unconditionally meant an admin's first paint showed a different real
    // person's name and DOB above this patient's directives — a
    // misidentification, not just a flicker. So the fallback is allowed only
    // when the session patient genuinely IS the target, and everything
    // identity-bearing withholds until then.
    //
    // NOT `selfScoped`. A caregiver resolves to a TENTATIVE self scope until an
    // async pid lookup downgrades them to ASSIGNED, and `allData.patient` is
    // the caregiver throughout that window — so keying the fallback on
    // self-scope alone painted their name and DOB over someone else's
    // directives for the length of two awaited round trips.
    identityFromSession() {
      return canUseSessionAsPatientIdentity({
        viewingPatientChart: this.viewingPatientChart,
        scope: this.scope,
        resolvedPatientPid: this.resolvedPatientPid,
      });
    },
    patientIdentityReady() {
      return !!(this.targetPatient && this.targetPatient.patient_name) || this.identityFromSession;
    },
    patientName() {
      if (this.targetPatient && this.targetPatient.patient_name) {
        return this.targetPatient.patient_name;
      }
      if (!this.identityFromSession) return "";
      const f = this.patient.firstName || "";
      const l = this.patient.lastName || "";
      const name = `${f} ${l}`.trim();
      return name || "Patient";
    },
    // What the banner actually renders: never blank, never the wrong person.
    patientNameDisplay() {
      if (this.patientIdentityReady) return this.patientName || "Patient";
      return this.loading ? "Loading…" : "Patient";
    },
    patientDob() {
      if (this.targetPatient && this.targetPatient.dob) return this.targetPatient.dob;
      return this.identityFromSession ? this.patient.dob || "" : "";
    },
    // ---- Full-screen code assigner (split view) props ----
    assignerRecordLabel() {
      const rec = this.assignCodesFor;
      if (!rec) return "";
      return rec.ingest_path === "upload" ? rec.form_label || "Uploaded document" : "Manual entry";
    },
    assignerDocUrl() {
      const rec = this.assignCodesFor;
      return rec && hasDocument(rec) ? this.documentUrl(rec) : "";
    },
    assignerDocContentType() {
      return this.assignCodesFor?.document_content_type || "";
    },
    assignerDocTitle() {
      const rec = this.assignCodesFor;
      return rec ? this.documentName(rec) : "";
    },
    assignerReviewDirectives() {
      const rec = this.assignCodesFor;
      return rec && Array.isArray(rec.directives) ? rec.directives : [];
    },
    assignerApproverLine() {
      const rec = this.assignCodesFor;
      if (!rec) return "";
      if (rec.approved_by_name) {
        return `Signed & Activated by ${rec.approved_by_name} · ${this.formatDate(rec.approved_at)}`;
      }
      return "Not yet signed & activated.";
    },
    patientMrn() {
      return (this.targetPatient && this.targetPatient.mrn) || "";
    },
    // One-line "who is this about" for overlay headers: name · MRN · DOB.
    // A raised modal outlives its context — someone gets interrupted and
    // returns to a form with no patient on it. Same readiness gate as the
    // banner so it never names the wrong person.
    patientIdentityLine() {
      if (!this.patientIdentityReady) return this.loading ? "Loading patient…" : "";
      const parts = [this.patientNameDisplay];
      const mrn = this.patientMrn || this.patientId;
      if (mrn) parts.push(`MRN ${mrn}`);
      if (this.patientDob) parts.push(`DOB ${this.formatDate(this.patientDob)}`);
      return parts.join(" · ");
    },
    // True when the logged-in user IS the patient THIS PAGE IS ABOUT. Two
    // conditions, both required:
    //   1. No separate clinician/manager identity in the session (presence of
    //      `manager.externid` is the established self-vs-clinician heuristic,
    //      mirroring PdfsProviderDocuments.vue's `if (res.data.manager)`).
    //   2. The signed-in person's OWN chart is the page's patient. On keyon
    //      that was the session psersonalID against the page's `mrn`; in the
    //      port the session profile names the person's own chart
    //      (`/me` patient_id), and the page's patient is its chart id —
    //      `allData.patient` is the OPEN chart, so it cannot say who is signed
    //      in. Without this check a patient-type session viewing ANOTHER
    //      patient's page was offered "Self (the patient)" for a record that
    //      isn't theirs.
    // Conservative before the chart id is known (-> false); the Add modal
    // opens only after records load, so the real self case still gets its
    // 'self' default.
    isSelf() {
      if (this.manager && this.manager.externid) return false;
      const ownPid = getOwnPatientPid(this.sessionMe);
      const pagePid = String(this.patientId || "");
      return !!ownPid && !!pagePid && ownPid === pagePid;
    },
    // The signed-in profile (`GET /me`): who is at the keyboard, whatever
    // chart is open.
    sessionMe() {
      return useSessionStore().me || null;
    },
    // The logged-in user's identity for action snapshots. Prefer MySQL
    // sessionSelf; NPI/licence still come from PHR manager/patient until those
    // fields live on GetSelfProfile.
    sessionActor() {
      return sessionActorFrom(this.allData, useSessionSelfStore().profile);
    },
    currentUserName() {
      return this.sessionActor.displayName || "Unknown user";
    },
    // The person at the keyboard: the staff member (`manager.externid`), or a
    // patient's own user id from the session profile — never the open chart's
    // id, which is what `patient.externId` is in the port. Compared with
    // `document_uploaded_by_id` to offer an uploader "Remove".
    currentUserId() {
      return this.manager.externid || getSessionUserId(this.sessionMe) || this.patient.externId || "";
    },
    currentUserNpi() {
      // The signing actor's NPI: the charting clinician when present, otherwise
      // the logged-in user's own NPI (self-login). Snapshotted as actor_npi /
      // approved_by_npi on the sign/reauth event.
      return this.manager.npi || this.manager.externid || this.patient.npi || "";
    },
    hasNpiPossession() {
      // Sign-off gate #2: credential possession. The signer is the charting
      // clinician (manager) when one is present, otherwise the logged-in user
      // themselves (self-login).
      //
      // The credential can live in EITHER of two stores and the session payload
      // only ever carries one of them:
      //   - pacmny-be Postgres LA_user.ZV_user_npi -> `patient.npi` below,
      //     written by /sessionless/register at account creation.
      //   - keyon account_user.proffesionalLicense -> `signerCredential`,
      //     written by the Add User / verify-provider onboarding flows.
      // Reading only the session payload blocked every clinician whose credential
      // was recorded in keyon (their Sign button sat disabled with a tooltip
      // naming the very field the gate could not see). keyon re-checks this
      // authoritatively on write; this is the UX pre-gate.
      const signer = this.manager && this.manager.externid ? this.manager : this.patient;
      const npi = signer.npi ? String(signer.npi).trim() : "";
      const lic = signer.proffesionalLicense ? String(signer.proffesionalLicense).trim() : "";
      return !!(npi || lic || this.signerCredential);
    },
    canRead() {
      return this.privilegesStore.effectiveGranted(PRIVILEGES.DIRECTIVE_READ);
    },
    canUploadModify() {
      return this.privilegesStore.effectiveGranted(PRIVILEGES.DIRECTIVE_UPLOAD_MODIFY);
    },
    /**
     * A patient submitting their own directive. Only ever true on their own
     * record — `isSelf` compares the session's psersonalID against the patient
     * on this page, and the server re-checks ownership independently.
     *
     * Deliberately narrower than canUploadModify: this permits creating, and
     * nothing else. Patients do not hold 53, so add-codes, confirm-authorization
     * and revoke stay closed — every one of those still reads canUploadModify.
     */
    canCreateOwn() {
      return (
        this.isSelf && this.privilegesStore.effectiveGranted(PRIVILEGES.DIRECTIVE_CREATE_OWN)
      );
    },
    /** Either route to the Add modal: staff on any record, or a patient on theirs. */
    canCreateRecord() {
      return this.canUploadModify || this.canCreateOwn;
    },
    canApproveReauthorize() {
      return this.privilegesStore.effectiveGranted(PRIVILEGES.DIRECTIVE_APPROVE_REAUTHORIZE);
    },
    // Sign requires BOTH gates: privilege AND NPI possession.
    canSign() {
      return this.canApproveReauthorize && this.hasNpiPossession;
    },
    defaultRelationship() {
      // Default to 'self' only when the logged-in user IS the patient; when a
      // clinician/manager is submitting on the patient's behalf, leave it unset
      // so they must actively pick a relationship (no misleading 'self'
      // default). The submitter can change it either way.
      return this.isSelf ? "self" : "";
    },
    // "<code_system>|<code>" -> catalog row, so a record's coded directives can be
    // enriched with full_name + description for the rich cards (the codes themselves
    // are snapshotted on the record; the catalog supplies the safety-display text).
    //
    // Keyed on system+code, not code alone: several categories have no SNOMED
    // concept and carry ERRIFF-LOCAL codes (issue #177), which could otherwise
    // collide with a real concept id and enrich a directive with the wrong text.
    catalogByCode() {
      const map = {};
      for (const row of getDirectiveCatalogMock()) {
        map[codeKey(row.code_system, row.code)] = row;
      }
      return map;
    },
    conflictMessage() {
      return CONFLICT_MESSAGE;
    },
    patientAge() {
      return ageInYears(this.patientDob);
    },
    // Two-letter avatar initials for the patient banner. Derived from the real
    // name only — initials of the wrong person are still the wrong person.
    patientInitials() {
      if (!this.patientIdentityReady) return "…";
      return this.initialsFromName(this.patientName) || "?";
    },
    // Active records get the "In force now" hero treatment; everything else
    // collapses into "History". `records` is already
    // sorted status-rank then date-desc, so these preserve that order.
    // Resolved from `records` by id rather than held as a reference: every
    // action reloads the list and replaces the record objects, so a stored
    // reference would go stale. Deleting the open record resolves to null,
    // which closes the modal on its own.
    infoRec() {
      if (!this.infoRecId) return null;
      return this.records.find((r) => r.id === this.infoRecId) || null;
    },
    // Resolved from records rather than held as an object, so it stays in step
    // with a reload (the freshly-added codes change what the button should say).
    docViewerRec() {
      if (!this.docViewerRecId) return null;
      return this.records.find((r) => r.id === this.docViewerRecId) || null;
    },
    // Modal actions, grouped by ACTOR and gated by CAPABILITY: a button the
    // user can never use (missing privilege / NPI) is absent, not disabled —
    // absence says "not your role", disabled says "you missed a step".
    clinicianActions() {
      const rec = this.infoRec;
      if (!rec) return [];
      const out = [];
      if (this.canSign && rec.status === DIRECTIVE_STATUS.PENDING_REVIEW) out.push("sign");
      if (this.canSign && rec.status === DIRECTIVE_STATUS.ACTIVE) out.push("reauthorize");
      if (this.canApproveReauthorize) out.push("reviewed", "review-needed");
      return out;
    },
    submissionActions() {
      const rec = this.infoRec;
      if (!rec) return [];
      const out = [];
      if (this.canUploadModify && rec.status === DIRECTIVE_STATUS.UNCONFIRMED_AUTHORIZATION) {
        out.push("confirm");
      }
      if (this.canAddDirectivesTo(rec)) out.push("add-directives");
      if (
        this.canUploadModify &&
        (rec.status === DIRECTIVE_STATUS.PENDING_REVIEW ||
          rec.status === DIRECTIVE_STATUS.UNCONFIRMED_AUTHORIZATION)
      ) {
        out.push("delete");
      }
      return out;
    },
    activeRecords() {
      return this.records.filter((r) => r.status === DIRECTIVE_STATUS.ACTIVE);
    },
    // Submissions rows: only what is still moving through the pipeline —
    // pending review / unconfirmed authorization (at most one, by the
    // creation gate). The active record renders once, as the hero at the top
    // of the same card, not as a row.
    inFlightRecords() {
      return this.records.filter((r) => this.isInFlight(r));
    },
    // History = taken-care-of submissions only (superseded / revoked /
    // expired). In-flight and active rows live in the Submissions card.
    otherRecords() {
      return this.records.filter(
        (r) => r.status !== DIRECTIVE_STATUS.ACTIVE && !this.isInFlight(r)
      );
    },
    hasActive() {
      return this.activeRecords.length > 0;
    },
    // Why the patient is Full Code — mirrors codeStatus.js's fullCodeReason
    // (the list page's copy), fed from this page's records instead of the
    // roster row. Settled-records-only patients get no line (per user, the
    // "No record has been validated" wording read as noise): Full Code stands
    // on its own unless something is actually awaiting sign-off.
    fullCodeBecause() {
      const pending = this.inFlightRecords.length;
      if (pending > 0) {
        return `${pending} record${pending === 1 ? "" : "s"} awaiting sign-off by an NPI holder.`;
      }
      if (this.records.length > 0) return "";
      return "No directive on file.";
    },
    // One-line rollup of the non-active records for the collapsed section header.
    otherSummary() {
      const counts = {};
      for (const r of this.otherRecords) {
        counts[r.status] = (counts[r.status] || 0) + 1;
      }
      return this.legendStatuses
        .filter((s) => s !== DIRECTIVE_STATUS.ACTIVE && counts[s])
        .map((s) => `${counts[s]} ${(this.statusLabels[s] || s).toLowerCase()}`)
        .join(" · ");
    },
  },
  watch: {
    /**
     * Same hard-reload race as DirectivesListPage: `created()` runs once, and
     * after a reload neither the session nor the privileges store has
     * rehydrated yet, so the gate fails closed and the chart never loads. Worse
     * here than on the roster — this page leaves `loading` true on that path,
     * so once the privileges landed and unblocked the template it painted a
     * spinner and "Loading patient details…" with nothing left to run.
     *
     * Re-run when the inputs land; a genuine refusal is not re-tried (see
     * shouldRetryDirectiveInit).
     */
    allData: {
      handler() {
        this.retryInitIfUnsettled();
      },
      deep: false,
    },
    "privilegesStore.loadedKey": function onPrivilegesLoaded() {
      this.retryInitIfUnsettled();
    },
    // Re-fetch when navigating between patients without unmounting the component.
    "$route.params.patientId": async function onPatientRouteChange(patientId) {
      if (this.accessBlocked) return;
      // Self-scoped sessions cannot browse other patients via URL tampering.
      if (this.selfScoped) {
        await this.ensureResolvedPatientPid();
        if (!this.resolvedPatientPid) {
          this.scope = { kind: SCOPE.ASSIGNED };
        } else if (patientId && String(patientId) !== this.resolvedPatientPid) {
          this.$router.replace("/directives/" + this.resolvedPatientPid);
          return;
        }
      }
      this.loadRecords();
    },
  },
  async created() {
    await this.runInit();
  },
  methods: {
    hasDocument,
    directiveDocumentState,
    /**
     * One entry point for both `created()` and the watchers, so a pass already
     * in flight is never doubled — it re-reads both inputs after each `await`
     * and will pick up whatever landed mid-flight on its own.
     */
    async runInit() {
      if (this.initialized || this.initializing) return;
      this.initializing = true;
      try {
        await this.init();
      } catch (e) {
        // Nothing below is allowed to strand the page in its loading state.
        // `loading` drives the spinner AND the banner's "Loading patient
        // details…", and only `loadRecords` clears it — so a throw on the way
        // there (the signer-credential or pid lookup) used to leave this chart
        // loading forever.
        console.error("[directives] init failed", e);
        this.loading = false;
      } finally {
        this.initializing = false;
      }
    },
    retryInitIfUnsettled() {
      const retry = shouldRetryDirectiveInit({
        initialized: this.initialized,
        initializing: this.initializing,
        allData: this.allData,
        privilegesStore: this.privilegesStore,
      });
      if (retry) this.runInit();
    },
    /**
     * Resolve scope and load the chart. Split out of `created()` so the
     * `allData` watcher can re-run it after a hard reload; `initialized`
     * guards the pair so only one of them loads.
     */
    async init() {
      if (this.initialized) return;
    // Privileges may already be loaded by the session sync; ensure they're present.
    if (this.allData && (!this.privilegesStore.loadedKey || this.privilegesStore.assignments.length === 0)) {
      try {
        await this.privilegesStore.ensureLoaded();
      } catch (e) {
        // non-fatal; gates simply stay closed
        console.warn("[directives] privilege load failed", e);
      }
    }
    // Resolve read scope from the ACTOR before any record is fetched. Must
    // come after the privilege load above: the "Administrators" group check
    // reads the store.
    try {
      this.scope = resolveDirectiveScope(this.allData, this.privilegesStore);
    } catch (e) {
      console.warn("[directives] scope resolution failed", e);
      this.scope = { kind: SCOPE.NONE, reason: "error" };
    }
    if (this.accessBlocked) {
      // Settled only if the gate had something to go on: with both inputs
      // present a refusal is a real answer and must not be retried on every
      // session change; without them it is a startup race.
      this.initialized = directiveGateInputsReady(this.allData, this.privilegesStore);
      // The blocked template replaces the chart entirely, so this spinner is
      // not on screen — but `accessBlocked` can flip to false underneath it
      // when the privileges land, and then it is.
      this.loading = false;
      return;
    }

    // keyon's copy of the signer's NPI/licence — the session payload carries
    // only pacmny-be's. Non-fatal: on failure the gate falls back to the
    // session value and keyon still enforces on write.
    await this.loadSignerCredential();
    // Resolve MySQL patients.pid before any record load when self-scoped.
    if (this.selfScoped) {
      const ok = await this.ensureResolvedPatientPid();
      if (!ok) {
        // Not a patient — a caregiver. They fall to ASSIGNED and may open the
        // route pid, because keyon checks panel membership on GetDirectiveRecords
        // and refuses one that is not theirs. A genuinely self-scoped session
        // still cannot reach here: it resolves a pid.
        this.scope = { kind: SCOPE.ASSIGNED };
      } else {
        const routePid = this.$route.params.patientId;
        if (!routePid || String(routePid) !== this.resolvedPatientPid) {
          this.initialized = true;
          this.$router.replace("/directives/" + this.resolvedPatientPid);
          return;
        }
      }
    }
    this.initialized = true;
    await this.loadRecords();
    },
    ...mapActions("login", ["updateApplicationModel"]),
    // Map session scope (accountnumber + psersonalID) to directive_record.patient_pid.
    async ensureResolvedPatientPid() {
      if (!this.selfScoped) return true;
      if (this.resolvedPatientPid) return true;
      const pid = await resolveDirectivePatientPid({
        accountnumber: getDirectiveAccountNumber(this.allData),
        psersonalId: getSessionPatientPersonalId(this.patient),
      });
      this.resolvedPatientPid = pid;
      return !!pid;
    },
    goToList() {
      this.$router.push("/directives");
    },
    async loadRecords() {
      this.loading = true;
      try {
        if (USE_MOCK_DATA) {
          this.records = sortDirectiveRecords(getDirectiveRecordsMock());
          this.targetPatient = null;
        } else {
          const res = await getDirectiveRecords({
            accountnumber: getDirectiveAccountNumber(this.allData),
            patientPid: this.patientId,
            actorId: this.currentUserId,
          });
          // The server is the authority on whose chart this is. Show its
          // refusal rather than an empty chart, which would read as "no
          // directives on file" — the opposite of the truth, and dangerous
          // here because no directives means Full Code.
          if (res.denied) {
            this.scope = { kind: SCOPE.NONE, reason: "not_assigned" };
            this.records = [];
            this.targetPatient = null;
            return;
          }
          this.records = res.records;
          this.targetPatient = res.patient;
        }
        // Every mutation (sign, re-authorize, confirm, revoke, delete) reloads
        // through here, so this one emit keeps the nav badge honest without
        // each handler having to remember. It also fires on first load, which
        // refreshes a count that may have gone stale in another session.
        EventBus.emit(DIRECTIVES_CHANGED);
      } catch (e) {
        console.error("[directives] load failed", e);
        this.$swal({ type: "error", title: "Error", text: "Failed to load directives." });
      } finally {
        this.loading = false;
      }
    },
    isExpanded(id) {
      return !this.collapsedIds.includes(id);
    },
    toggleExpanded(id) {
      const i = this.collapsedIds.indexOf(id);
      if (i === -1) this.collapsedIds.push(id);
      else this.collapsedIds.splice(i, 1);
    },
    openInfo(rec) {
      this.infoRecId = rec.id;
    },
    closeInfo() {
      this.infoRecId = null;
      this.cancelReviewNeeded();
    },
    // ---- Guided actions: lifecycle position -> one next-step sentence ----
    // The open review flag: the latest 'review_needed' event not yet followed
    // by a 'reviewed' event. Derived from the append-only audit trail — the
    // events are never mutated, only the display model reads state into them.
    openReviewFlag(rec) {
      if (!rec || !Array.isArray(rec.events)) return null;
      const flag = this.latestEventOfType(rec, "review_needed");
      if (!flag) return null;
      const reviewed = this.latestEventOfType(rec, "reviewed");
      if (reviewed && new Date(reviewed.occurred_at) >= new Date(flag.occurred_at)) return null;
      return flag;
    },
    lastReviewedEvent(rec) {
      return this.latestEventOfType(rec, "reviewed");
    },
    latestEventOfType(rec, type) {
      if (!rec || !Array.isArray(rec.events)) return null;
      let latest = null;
      for (const ev of rec.events) {
        if (ev.event_type !== type) continue;
        if (!latest || new Date(ev.occurred_at) > new Date(latest.occurred_at)) latest = ev;
      }
      return latest;
    },
    // One sentence: where the record is, what happens next, and WHOSE turn it
    // is. Empty string when the record is settled and nothing is pending.
    nextStepText(rec) {
      if (!rec) return "";
      if (rec.status === DIRECTIVE_STATUS.UNCONFIRMED_AUTHORIZATION) {
        return this.canUploadModify
          ? "Next step: confirm this document's legal authorization (below), so it can reach clinician sign-off."
          : "Waiting for the document's legal authorization to be confirmed before it can reach clinician sign-off.";
      }
      if (rec.status === DIRECTIVE_STATUS.PENDING_REVIEW) {
        if (this.openReviewFlag(rec)) {
          return this.canApproveReauthorize
            ? "A review flag is open (below) — address it, mark the record reviewed, then sign to activate."
            : "A review flag is open. Waiting for a clinician to resolve it and sign.";
        }
        return this.canSign
          ? "Next step: review the directives below, then Sign & Activate to put them in force."
          : "Waiting for a clinician (NPI holder) to sign. There is nothing you need to do here.";
      }
      if (rec.status === DIRECTIVE_STATUS.ACTIVE && this.reauthOverdue(rec)) {
        return this.canSign
          ? "Next step: re-authorization is overdue — re-authorize to keep this record current."
          : "Re-authorization is overdue. Waiting for an NPI holder to re-authorize.";
      }
      return "";
    },
    // Submissions-card mirror of the same idea: when the in-flight row's
    // needed action is not yours, say whose turn it is instead of showing
    // nothing next to "More actions".
    rowWaitingText(rec) {
      if (rec.status === DIRECTIVE_STATUS.PENDING_REVIEW && !this.canSign) {
        return "Waiting for a clinician to sign";
      }
      if (rec.status === DIRECTIVE_STATUS.UNCONFIRMED_AUTHORIZATION && !this.canUploadModify) {
        return "Waiting for authorization confirmation";
      }
      return "";
    },
    // Whether the record has a source document worth linking to.
    documentLinksFor(rec) {
      return !!(rec && rec.ingest_path === "upload" && hasDocument(rec));
    },
    // An upload's document area shows while it has a document, and after one
    // was removed ("Document unavailable"); a manual record has none.
    documentAreaFor(rec) {
      return !!(rec && rec.ingest_path === "upload" && directiveDocumentState(rec) !== "none");
    },
    // Remove is offered on a pre-signature upload's document to its uploader or
    // a holder of upload/modify (53). keyon decides, and also requires read
    // scope for the patient; a signed or closed record keeps its document.
    canRemoveDocument(rec) {
      return canDeleteDirectiveDocument(rec, { viewerUid: this.currentUserId, canUploadModify: this.canUploadModify });
    },
    openRemoveDocument(rec) {
      if (!this.canRemoveDocument(rec)) return;
      this.removeDocumentError = "";
      this.removeDocumentRec = rec;
    },
    closeRemoveDocument() {
      if (this.removeDocumentBusy) return;
      this.removeDocumentRec = null;
      this.removeDocumentError = "";
    },
    async confirmRemoveDocument(reason) {
      const rec = this.removeDocumentRec;
      if (!rec || this.removeDocumentBusy) return;
      this.removeDocumentBusy = true;
      this.removeDocumentError = "";
      try {
        const r = await deleteDirectiveDocument({ directive_record_id: rec.id, file_uid: rec.document_file_uid, reason });
        if (!r.ok) {
          this.removeDocumentError = r.message || "Could not remove the document.";
          return;
        }
        this.removeDocumentRec = null;
        if (this.docViewerRecId === rec.id) this.showDocViewer = false;
        // keyon closed the submission with the removal, so the patient's in-flight
        // slot is free and uploading again is the next step.
        this.$swal({
          type: "success",
          title: "Document removed",
          text: DIRECTIVE_DOCUMENT_REMOVED_TEXT,
        });
        await this.loadRecords();
      } catch (e) {
        console.error("[directives] remove document failed", e);
        this.removeDocumentError = "Could not remove the document.";
      } finally {
        this.removeDocumentBusy = false;
      }
    },
    // Where the record came from — "Manual entry" or the uploaded form's label.
    // Card chrome no longer states this; the info modal does.
    provenanceText(rec) {
      if (!rec) return "";
      return rec.ingest_path === "upload" ? rec.form_label || "Uploaded document" : "Manual entry";
    },
    badgeClass(status) {
      return {
        active: status === DIRECTIVE_STATUS.ACTIVE,
        pending: status === DIRECTIVE_STATUS.PENDING_REVIEW,
        unconfirmed: status === DIRECTIVE_STATUS.UNCONFIRMED_AUTHORIZATION,
        expired: status === DIRECTIVE_STATUS.EXPIRED,
        superseded: status === DIRECTIVE_STATUS.SUPERSEDED,
        revoked: status === DIRECTIVE_STATUS.REVOKED,
      };
    },
    statusDot(status) {
      return STATUS_DOTS[status] || "#a0aec0";
    },
    // Enrich a record's snapshotted directive with catalog full_name + description
    // for the rich card. Falls back to the record's own label when the code isn't
    // in the curated catalog (e.g. a future full-SNOMED pick).
    enrichDirective(d) {
      const cat = this.catalogByCode[codeKey(d.code_system, d.code)];
      return {
        ...d,
        name: (cat && cat.full_name) || d.label,
        description: cat && cat.description ? cat.description : "",
        category_code: cat ? cat.category_code : null,
      };
    },
    // Soft conflict flag for a record's coded directives (e.g. OCR surfaced
    // contradictory candidates). Maps each snapshot to its catalog category.
    recordConflict(rec) {
      const cats = (rec.directives || [])
        .map((d) => {
          const cat = this.catalogByCode[codeKey(d.code_system, d.code)];
          return cat ? cat.category_code : null;
        })
        .filter(Boolean);
      return hasConflict(cats);
    },
    // Two-letter initials from a person's name (drops any "(...)" suffix and
    // an honorific like "Dr.").
    initialsFromName(name) {
      const clean = String(name || "")
        .replace(/\(.*\)/, "")
        .replace(/^\s*Dr\.?\s*/i, "")
        .trim();
      if (!clean) return "";
      return clean
        .split(/\s+/)
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase();
    },
    signerInitials(rec) {
      return this.initialsFromName(rec.approved_by_name) || "—";
    },
    // Clinical direction of a coded directive, for the limiting(red)/affirming(green)
    // accent in the hero cards. Affirming = full code or an "…acceptable" choice.
    codeAccent(d) {
      const cat = this.catalogByCode[codeKey(d.code_system, d.code)];
      const code = (cat && cat.category_code) || "";
      const label = ((cat && cat.choice_label) || d.label || "").toLowerCase();
      const affirming = code === "full_code" || /acceptable/.test(label);
      return affirming ? "affirming" : "limiting";
    },
    // Short glyph for a code's icon chip (parenthetical abbreviation if present,
    // else an initialism of the choice label).
    codeGlyph(d) {
      const cat = this.catalogByCode[codeKey(d.code_system, d.code)];
      const label = (cat && cat.choice_label) || d.label || "";
      const paren = label.match(/\(([^)]+)\)/);
      if (paren) return paren[1];
      return (
        label
          .replace(/\(.*\)/, "")
          .split(/\s+/)
          .map((w) => w[0])
          .join("")
          .slice(0, 4)
          .toUpperCase() || "•"
      );
    },
    // Color-coded re-authorization pill for the active hero footer. Returns null
    // when there's no re-auth window to show.
    /** e.g. "Arizona — re-authorize every 60 days". No rule => no deadline. */
    governingRuleText(rec) {
      if (!rec || !rec.governing_state) return "";
      const name = stateNameForCode(rec.governing_state);
      const days = Number(rec.reauth_days);
      if (!Number.isFinite(days) || days <= 0) {
        return `${name} — no re-authorization required`;
      }
      return `${name} — re-authorize every ${days} days`;
    },
    /** The window is whatever the governing state's rule says — not a fixed 90
        days. When no rule governs the record, re-authorizing records the act but
        sets no new deadline, and the copy says exactly that. */
    reauthConfirmText(rec) {
      const days = Number(rec && rec.reauth_days);
      if (Number.isFinite(days) && days > 0) {
        const where = rec.governing_state ? ` (${stateNameForCode(rec.governing_state)})` : "";
        return `This resets the ${days}-day re-authorization window${where}. Your name and NPI will be recorded.`;
      }
      return "This records a re-authorization. No state rule currently sets a re-authorization deadline for this record. Your name and NPI will be recorded.";
    },
    reauthPill(rec) {
      if (!rec || !rec.reauth_due_at) return null;
      const due = this.formatDate(rec.reauth_due_at);
      if (this.reauthOverdue(rec)) {
        return { cls: "overdue", icon: "⚠", text: `Re-authorization Expired! · was due ${due}` };
      }
      if (this.reauthDueSoon(rec)) {
        return { cls: "soon", icon: "⚠", text: `Re-auth due ${due} · in ${this.reauthDaysLeft(rec)} days` };
      }
      return { cls: "ok", icon: "↻", text: `Re-auth due ${due}` };
    },
    toggleLegend() {
      this.legendOpen = !this.legendOpen;
    },
    toggleOther() {
      this.otherOpen = !this.otherOpen;
    },
    // Per-record health-care-proxy notice (attestation block).
    proxyNotice(rec) {
      return rec.is_legal_surrogate && rec.authority_type === AUTHORITY_TYPE.HEALTH_CARE_PROXY;
    },
    // Widget title for a submission without a document: a manual entry is
    // named as such; an upload missing its path falls back to documentName.
    submissionName(rec) {
      if (rec && rec.ingest_path === "manual") return "Manual entry";
      return this.documentName(rec);
    },
    // Still moving through the pipeline: awaiting sign-off or awaiting
    // authorization confirmation.
    isInFlight(rec) {
      return (
        rec.status === DIRECTIVE_STATUS.PENDING_REVIEW ||
        rec.status === DIRECTIVE_STATUS.UNCONFIRMED_AUTHORIZATION
      );
    },
    // Inline Sign only when the click would actually sign: pending-review
    // record + both sign gates (privilege AND NPI possession).
    rowSignable(rec) {
      return rec.status === DIRECTIVE_STATUS.PENDING_REVIEW && this.canSign;
    },
    rowConfirmable(rec) {
      return (
        rec.status === DIRECTIVE_STATUS.UNCONFIRMED_AUTHORIZATION && this.canUploadModify
      );
    },
    // Stored document name (for the file tiles).
    documentName(rec) {
      return directiveDocumentName(rec);
    },
    // Uppercase file extension for the widget/drawer chips (e.g. PDF, JPG).
    fileExt(rec) {
      return directiveDocumentExt(rec);
    },
    // Collapsed-row right-hand caption/value: approval state at a glance.
    approvalCaption(rec) {
      return rec.status === DIRECTIVE_STATUS.ACTIVE ? "Approved" : "Approval";
    },
    approvalText(rec) {
      return rec.approved_at ? this.formatDate(rec.approved_at) : "Pending";
    },
    summarizeDirectives(rec) {
      if (!rec.directives || rec.directives.length === 0) {
        return rec.ingest_path === "upload" ? "Document only — no codes yet" : "No codes";
      }
      const labels = rec.directives.map((d) => d.label);
      return labels.length <= 2 ? labels.join(", ") : `${labels.slice(0, 2).join(", ")} +${labels.length - 2} more`;
    },
    relationshipLabel(rec) {
      if (rec.relationship_to_patient === "other" && rec.relationship_other) {
        return `Other — ${rec.relationship_other}`;
      }
      return this.relationshipLabels[rec.relationship_to_patient] || rec.relationship_to_patient;
    },
    documentUrl(rec) {
      // Opaque uid, served by keyon file.php after session + MFA + directive scope.
      return directiveDocumentUrl(rec);
    },
    // Open a record's uploaded document in the inline viewer (same route) instead
    // of a new browser tab. The url may be "" (no usable uid); the viewer then
    // shows "Document unavailable".
    openDocument(rec) {
      const viewer = directiveViewerFor(rec);
      if (!viewer) return;
      this.docViewerUrl = viewer.url;
      this.docViewerContentType = viewer.contentType;
      this.docViewerTitle = viewer.title;
      // Remembered so the viewer's "Add directives" button knows which record
      // it is coding — the URL alone can't be mapped back to one.
      this.docViewerRecId = viewer.recId;
      this.showDocViewer = true;
    },
    sortedEvents(rec) {
      const evs = Array.isArray(rec.events) ? rec.events.slice() : [];
      return evs.sort((a, b) => new Date(b.occurred_at) - new Date(a.occurred_at));
    },
    eventStatusHint(ev) {
      // Maps an event type to a badge color for the trail dot.
      const map = {
        signed: DIRECTIVE_STATUS.ACTIVE,
        reauthorized: DIRECTIVE_STATUS.ACTIVE,
        created: DIRECTIVE_STATUS.PENDING_REVIEW,
        authorization_confirmed: DIRECTIVE_STATUS.PENDING_REVIEW,
        reviewed: DIRECTIVE_STATUS.PENDING_REVIEW,
        review_needed: DIRECTIVE_STATUS.EXPIRED,
        superseded: DIRECTIVE_STATUS.SUPERSEDED,
        revoked: DIRECTIVE_STATUS.REVOKED,
      };
      return map[ev.event_type] || DIRECTIVE_STATUS.PENDING_REVIEW;
    },
    reauthOverdue(rec) {
      return (
        rec.status === DIRECTIVE_STATUS.ACTIVE &&
        rec.reauth_due_at &&
        new Date(rec.reauth_due_at).getTime() < Date.now()
      );
    },
    // Days until re-auth is due (negative if overdue).
    reauthDaysLeft(rec) {
      if (!rec.reauth_due_at) return null;
      const ms = new Date(rec.reauth_due_at).getTime() - Date.now();
      return Math.ceil(ms / (1000 * 60 * 60 * 24));
    },
    // Soft reminder: active, not yet overdue, due within 14 days.
    reauthDueSoon(rec) {
      if (rec.status !== DIRECTIVE_STATUS.ACTIVE || !rec.reauth_due_at) return false;
      const days = this.reauthDaysLeft(rec);
      return days !== null && days >= 0 && days <= 14;
    },
    formatDate(d) {
      return formatDateValue(d);
    },
    formatDateTime(d) {
      if (!d) return "—";
      return new Date(d).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    },

    // ---- Add modal ----
    async openAddModal() {
      // One in-flight record per patient (hard block, no escape hatch):
      // creating another pending submission would start exactly the train of
      // pending-review records this rule exists to prevent. The path forward
      // is resolving the existing one — sign, confirm, or delete it. keyon
      // enforces the same rule server-side.
      const inFlight = this.records.find(
        (r) =>
          r.status === DIRECTIVE_STATUS.PENDING_REVIEW ||
          r.status === DIRECTIVE_STATUS.UNCONFIRMED_AUTHORIZATION
      );
      if (inFlight) {
        // Worded for who is being refused. A patient holds none of the
        // privileges the staff message tells them to use — "review, sign, or
        // delete it" sends them looking for controls that are not there — and
        // what they need to hear is that their wishes ARE recorded and are
        // waiting on a clinician. Matches keyon's server-side wording so the
        // two cannot drift.
        const submitterIsPatient = this.canCreateOwn && !this.canUploadModify;
        if (submitterIsPatient) {
          await this.$swal({
            type: "info",
            title: "A directive is already awaiting review",
            text:
              "You already have a directive awaiting review. Please wait for " +
              "your clinician to sign it before adding another.",
          });
          return;
        }
        const result = await this.$swal({
          type: "info",
          title: "A directive is already pending",
          text:
            `${this.patientNameDisplay} already has a directive record awaiting ` +
            `${inFlight.status === DIRECTIVE_STATUS.PENDING_REVIEW ? "sign-off" : "authorization confirmation"}. ` +
            "Review, sign, or delete it before creating a new one.",
          showCancelButton: true,
          confirmButtonText: "Open pending record",
        });
        if (result && result.isConfirmed) this.openInfo(inFlight);
        return;
      }
      this.showAddModal = true;
    },
    async onAddSubmit(payload) {
      this.saving = true;
      try {
        const base = {
          accountnumber: getDirectiveAccountNumber(this.allData),
          patientPid: this.patientId,
          createdById: this.currentUserId,
          createdByName: this.currentUserName,
          relationshipToPatient: payload.relationshipToPatient,
          relationshipOther: payload.relationshipOther,
          isLegalSurrogate: payload.isLegalSurrogate,
          authorityType: payload.authorityType,
        };
        let result;
        if (payload.method === "upload") {
          result = await createUploadDirectiveRecord({
            ...base,
            file: payload.file,
            documentAuthorized: payload.documentAuthorized,
            formLabel: payload.formLabel,
          });
        } else {
          result = await createManualDirectiveRecord({ ...base, directives: payload.directives });
        }
        // The endpoint returns { status: 'Successful', record_id } on success, or
        // { status: 'Error', message } otherwise. Don't fake success on a server error.
        const ok = result && (result.status === "Successful" || result.status === "200-Successfull");
        if (!ok) {
          this.$swal({
            type: "warning",
            title: "Not saved",
            text: (result && result.message) || "The record could not be created. Please try again.",
          });
          return;
        }
        this.showAddModal = false;
        this.$swal({
          type: "success",
          title: "Submitted",
          text: "The directive record was created and is awaiting clinician sign-off.",
        });
        await this.loadRecords();
      } catch (e) {
        console.error("[directives] create failed", e);
        this.$swal({
          type: "warning",
          title: "Not saved",
          text: refusalMessage(e?.response?.data) || "Something went wrong creating the record. Please try again.",
        });
      } finally {
        this.saving = false;
      }
    },

    // keyon holds a separate copy of the signer's NPI / professional license
    // (account_user.proffesionalLicense) that the pacmny-be session payload does
    // not carry. Both feed hasNpiPossession.
    async loadSignerCredential() {
      try {
        const { accountnumber, unique_ID } = resolveKeyonPrivilegeIdentity(this.allData);
        this.signerCredential = await getDirectiveSignerCredential({
          accountnumber,
          uniqueId: unique_ID,
        });
      } catch (e) {
        this.signerCredential = "";
        console.warn("[directives] signer credential load failed", e);
      }
    },
    // A reviewer with upload/modify (53) may code an UPLOADED record any time
    // before it is signed — repeatably, so a document whose codes were captured
    // incompletely can be completed. Deliberately narrower than that on two
    // axes, mirroring the server's own guard:
    //   - uploads only: a manual record already required >=1 code at entry and
    //     has no carve-out from "content is frozen at ingest".
    //   - pre-activation only: once active, the way to change content is to
    //     supersede the record, not edit it.
    canAddDirectivesTo(rec) {
      if (!rec || !this.canUploadModify) return false;
      if (rec.ingest_path !== "upload") return false;
      return (
        rec.status === DIRECTIVE_STATUS.PENDING_REVIEW ||
        rec.status === DIRECTIVE_STATUS.UNCONFIRMED_AUTHORIZATION
      );
    },
    onAddDirectives(rec) {
      if (!this.canAddDirectivesTo(rec)) return;
      this.openAssigner(rec, "add");
    },
    async addDirectives(rec, directives) {
      try {
        await addDirectivesToRecord({
          recordId: rec.id,
          actorId: this.currentUserId,
          actorName: this.currentUserName,
          actorNpi: this.currentUserNpi,
          directives,
        });
        this.$swal({
          type: "success",
          title: "Directives added",
          text:
            directives.length === 1
              ? "1 directive was added to this record."
              : `${directives.length} directives were added to this record.`,
        });
        await this.loadRecords();
      } catch (e) {
        // Same reasoning as signRecord: keyon reports refusals as HTTP 200
        // errors, so surface the server's message instead of claiming success.
        console.error("[directives] add directives failed", e);
        this.$swal({
          type: "warning",
          title: "Not added",
          text: (e && e.message) || "Something went wrong. Please try again.",
        });
        await this.loadRecords();
      }
    },
    // ---- Lifecycle actions ----
    async onSign(rec) {
      if (!this.canSign) return;
      // A record must have >=1 coded directive to become active. Document-only
      // records (0 codes) get codes assigned in the full-screen split view
      // (DirectivePicker beside the uploaded document) — then sign on confirm.
      // Records that already carry codes sign directly after a confirm.
      if (!rec.directives || rec.directives.length === 0) {
        this.openAssigner(rec, "assign");
        return;
      }
      const result = await this.$swal({
        type: "warning",
        title: "Sign & activate this directive?",
        text: "Your name and NPI will be recorded against this clinical sign-off.",
        showCancelButton: true,
      });
      if (!result || !result.isConfirmed) return;
      await this.signRecord(rec, []);
    },
    // Open the full-screen split view: 'assign' to code + sign a document-only
    // record, 'review' to read existing codes alongside the source document.
    openAssigner(rec, mode) {
      this.assignCodesFor = rec;
      this.assignerMode = mode || "assign";
      this.showAssignModal = true;
    },
    closeAssigner() {
      this.showAssignModal = false;
      this.assignCodesFor = null;
      this.assignerMode = "assign";
    },
    async onAssignConfirm(directives) {
      // The split view's footer button IS the confirmation — act directly on the
      // chosen codes (no second prompt). Which act depends on the mode: 'assign'
      // codes AND signs in one transaction, 'add' only codes and leaves the
      // record's status exactly where it was.
      const rec = this.assignCodesFor;
      const mode = this.assignerMode;
      this.closeAssigner();
      if (!rec || !Array.isArray(directives) || directives.length === 0) return;
      if (mode === "add") {
        await this.addDirectives(rec, directives);
        return;
      }
      await this.signRecord(rec, directives);
    },
    async signRecord(rec, assignedDirectives) {
      try {
        await signDirectiveRecord({
          recordId: rec.id,
          actorId: this.currentUserId,
          actorName: this.currentUserName,
          actorNpi: this.currentUserNpi,
          assignedDirectives,
        });
        this.$swal({ type: "success", title: "Signed", text: "Directive activated." });
        await this.loadRecords();
      } catch (e) {
        // signDirectiveRecord throws on a server refusal (missing privilege, no
        // credential, record no longer pending). Show that reason — reporting
        // "Signed" on a rejected write hid real failures completely.
        console.error("[directives] sign failed", e);
        this.$swal({
          type: "warning",
          title: "Not signed",
          text: (e && e.message) || "Something went wrong. Please try again.",
        });
        await this.loadRecords();
      }
    },
    // Move an uploaded document out of unconfirmed_authorization once someone
    // has confirmed it really is authorized/notarized, so it can reach sign-off.
    // Requires upload/modify (53) — a records act, not a clinical attestation.
    async onConfirmAuthorization(rec) {
      if (!this.canUploadModify) return;
      const result = await this.$swal({
        type: "question",
        title: "Confirm this document is authorized?",
        text:
          "This records that the uploaded document is fully authorized / notarized " +
          "and moves it to Pending Review for clinician sign-off.",
        showCancelButton: true,
        confirmButtonText: "Confirm authorization",
      });
      if (!result || !result.isConfirmed) return;
      try {
        await confirmDirectiveAuthorization({
          recordId: rec.id,
          actorId: this.currentUserId,
          actorName: this.currentUserName,
          actorNpi: this.currentUserNpi,
        });
        this.$swal({
          type: "success",
          title: "Authorization confirmed",
          text: "The document is now pending clinician sign-off.",
        });
      } catch (e) {
        console.error("[directives] confirm authorization failed", e);
        this.$swal({
          type: "warning",
          title: "Not confirmed",
          text: (e && e.message) || "Something went wrong. Please try again.",
        });
      }
      await this.loadRecords();
    },
    async onReviewed(rec) {
      // A review moments after the last one is almost always a double-click
      // or button-mashing, not a second clinical review — confirm before
      // stamping another audit event. (Repeat reviews later remain one click.)
      const last = this.lastReviewedEvent(rec);
      if (last && !this.openReviewFlag(rec)) {
        const minutes = Math.round((Date.now() - new Date(last.occurred_at)) / 60000);
        if (minutes < 10) {
          const result = await this.$swal({
            type: "question",
            title: "Reviewed again?",
            text:
              `${last.actor_name || "Someone"} marked this reviewed ` +
              `${minutes <= 1 ? "moments" : `${minutes} minutes`} ago. Record another review?`,
            showCancelButton: true,
            confirmButtonText: "Record review",
          });
          if (!result || !result.isConfirmed) return;
        }
      }
      try {
        await markDirectiveReviewed({
          recordId: rec.id,
          actorId: this.currentUserId,
          actorName: this.currentUserName,
        });
        this.$swal({ type: "success", title: "Reviewed", text: "Marked as reviewed." });
        await this.loadRecords();
      } catch (e) {
        console.error("[directives] review failed", e);
        this.$swal({ type: "warning", title: "Not saved", text: "The review endpoint is not implemented yet." });
      }
    },
    async onReauthorize(rec) {
      if (!this.canSign) return; // same privilege + NPI gate as sign-off
      const result = await this.$swal({
        type: "question",
        title: "Re-authorize this directive?",
        text: this.reauthConfirmText(rec),
        showCancelButton: true,
      });
      if (!result || !result.isConfirmed) return;
      try {
        const res = await reauthorizeDirectiveRecord({
          recordId: rec.id,
          actorId: this.currentUserId,
          actorName: this.currentUserName,
          actorNpi: this.currentUserNpi,
        });
        const ok = res && (res.status === "Successful" || res.status === "200-Successfull");
        if (!ok) {
          this.$swal({ type: "warning", title: "Not re-authorized", text: (res && res.message) || "Could not re-authorize." });
          return;
        }
        this.$swal({ type: "success", title: "Re-authorized", text: "The re-authorization window has been reset." });
        await this.loadRecords();
      } catch (e) {
        console.error("[directives] reauthorize failed", e);
        this.$swal({ type: "warning", title: "Not re-authorized", text: "Something went wrong. Please try again." });
      }
    },
    async onDelete(rec) {
      if (!this.canUploadModify) return;
      // Scope stated concretely: WHOSE submission, WHAT it contains, and that
      // nothing else is touched — "delete" must not be readable as "delete
      // all of this patient's directives".
      const n = Array.isArray(rec.directives) ? rec.directives.length : 0;
      const codes = n ? ` It contains ${n} coded ${n === 1 ? "directive" : "directives"} (${this.summarizeDirectives(rec)}).` : "";
      const result = await this.$swal({
        type: "warning",
        title: "Delete this pending submission?",
        text:
          `This deletes the ${this.statusLabels[rec.status] || "pending"} submission for ` +
          `${this.patientNameDisplay}.${codes} The patient's other directive records are ` +
          "not affected. For HIPAA compliance the deleted record and its history are " +
          "retained as revoked, not erased.",
        showCancelButton: true,
        confirmButtonText: "Delete",
      });
      if (!result || !result.isConfirmed) return;
      try {
        const res = await revokeDirectiveRecord({
          recordId: rec.id,
          actorId: this.currentUserId,
          actorName: this.currentUserName,
          actorNpi: this.currentUserNpi,
        });
        const ok = res && (res.status === "Successful" || res.status === "200-Successfull");
        if (!ok) {
          this.$swal({ type: "warning", title: "Not deleted", text: (res && res.message) || "Could not delete the entry." });
          return;
        }
        this.$swal({ type: "success", title: "Deleted", text: "The entry was removed (retained for audit)." });
        await this.loadRecords();
      } catch (e) {
        console.error("[directives] delete failed", e);
        this.$swal({ type: "warning", title: "Not deleted", text: "Something went wrong. Please try again." });
      }
    },
    openReviewNeeded(rec) {
      this.reviewNeededFor = rec.id;
      // Editing an open flag starts from its current note, not a blank box.
      const flag = this.openReviewFlag(rec);
      this.reviewNote = (flag && flag.note) || "";
    },
    cancelReviewNeeded() {
      this.reviewNeededFor = null;
      this.reviewNote = "";
    },
    async submitReviewNeeded(rec) {
      if (!this.reviewNote) return;
      try {
        await flagDirectiveReviewNeeded({
          recordId: rec.id,
          actorId: this.currentUserId,
          actorName: this.currentUserName,
          note: this.reviewNote,
        });
        this.$swal({ type: "success", title: "Flagged", text: "Marked as needing review." });
        this.cancelReviewNeeded();
        await this.loadRecords();
      } catch (e) {
        console.error("[directives] flag failed", e);
        this.$swal({ type: "warning", title: "Not saved", text: "The review-needed endpoint is not implemented yet." });
      }
    },
  },
};
</script>

<style scoped>
.dir-page {
  min-height: 100vh;
  background: var(--eh-paper);
  padding: 0;
  color: var(--eh-teal-ink);
  /* Single horizontal gutter for the page. The utility bar and the main column
     used to hard-code their own (28/28 desktop, 16 vs 14 on phones), so the
     "Back to patients" / "Add New Directives" row sat 2px off the card edges
     below it. One variable means they cannot drift apart again. */
  --dir-gutter: 28px;
}
.dir-back-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  color: var(--eh-teal);
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  padding: 0;
  margin-bottom: 8px;
}
.dir-back-link:hover {
  text-decoration: underline;
}
.dir-add-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 11px 18px;
  background: var(--eh-teal);
  color: var(--eh-white);
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
}
.dir-add-btn:hover {
  background: var(--eh-teal-deep);
}

/* Header widgets */

/* Legend */

/* Badges */
.dir-badge {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.2px;
  white-space: nowrap;
}
.dir-badge-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: none;
}
.dir-badge.sm {
  padding: 3px 9px;
  font-size: 11px;
  font-weight: 600;
}
.dir-badge.active {
  background: #c6f6d5;
  color: #22543d;
}
.dir-badge.pending {
  background: #bee3f8;
  color: #2a4365;
}
.dir-badge.unconfirmed {
  background: #feebc8;
  color: #7b341e;
}
.dir-badge.expired {
  background: #e2e8f0;
  color: #4a5568;
}
.dir-badge.superseded {
  background: #e9d8fd;
  color: #44337a;
}
.dir-badge.revoked {
  background: #fed7d7;
  color: #742a2a;
}

/* Loading / empty */
.dir-loading,
.dir-empty {
  text-align: center;
  padding: 70px 20px;
  color: var(--eh-muted);
}
.dir-empty .glyphicon {
  font-size: 40px;
  color: var(--eh-muted-border);
}
.dir-empty h3 {
  margin: 14px 0 6px;
  color: var(--eh-teal-ink);
}
/* `.dir-add-btn` is `display: flex`, so it is a block-level box and the parent's
   `text-align: center` cannot move it — it sat left of the icon and heading it
   was meant to line up under. `inline-flex` lets it shrink-wrap so the parent's
   centering applies, while keeping the icon/label row layout. */
.dir-empty .dir-add-btn {
  display: inline-flex;
  justify-content: center;
  margin-top: 18px;
}
.dir-loader {
  width: 44px;
  height: 44px;
  margin: 0 auto 14px;
  border: 4px solid var(--eh-divider);
  border-top-color: var(--eh-teal);
  border-radius: 50%;
  animation: dir-spin 1s linear infinite;
}

/* Records */

/* Detail */
.dir-detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-top: 14px;
}
.dir-detail-col h4 {
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--eh-muted);
  margin: 14px 0 8px;
}
.dir-detail-col h4:first-child {
  margin-top: 0;
}
.dir-detail-col p {
  font-size: 14px;
  color: var(--eh-teal-ink);
  margin: 0;
}
.dir-pill {
  font-size: 10px;
  text-transform: uppercase;
  background: var(--eh-muted-fill);
  color: var(--eh-muted);
  padding: 2px 7px;
  border-radius: 999px;
  font-weight: 700;
}
.dir-pill.warn {
  background: color-mix(in srgb, var(--eh-amber) 18%, var(--eh-white));
  color: var(--eh-teal-ink);
}
.dir-doc-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--eh-teal);
  font-weight: 600;
  text-decoration: none;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  font-family: inherit;
  font-size: inherit;
}
.dir-doc-link:hover {
  text-decoration: underline;
}
.dir-doc-link.dir-doc-remove {
  color: #b42318;
}
.dir-doc-state {
  color: var(--eh-muted);
  font-size: 14px;
  font-style: italic;
}
.dir-muted {
  color: var(--eh-muted);
  font-size: 14px;
}
.dir-muted.sm {
  font-size: 12px;
  margin-top: 6px;
}

/* Records section header */

/* Coded directive cards */
.dir-pending-box {
  margin-bottom: 18px;
  padding: 13px 15px;
  border: 1px dashed var(--eh-muted-border);
  border-radius: 10px;
  background: var(--eh-muted-fill);
  font-size: 13px;
  color: var(--eh-muted);
  line-height: 1.45;
}
.dir-code-cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 18px;
}
.dir-code-card {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  padding: 13px 15px;
  border: 1px solid var(--eh-divider);
  border-left: 3px solid var(--eh-teal);
  border-radius: 10px;
  background: var(--eh-muted-fill);
}
.dir-code-card-body {
  min-width: 0;
  flex: 1 1 auto;
}
.dir-candidate-flag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 8px;
}
.dir-match-confidence {
  display: inline-flex;
  align-items: center;
  margin-top: 8px;
  margin-left: 6px;
  background: var(--eh-teal-wash);
  color: var(--eh-teal);
  border: 1px solid var(--eh-teal);
}
.dir-conflict-warning {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 12px 0 0;
  padding: 10px 12px;
  background: color-mix(in srgb, var(--eh-amber) 18%, var(--eh-white));
  border: 1px solid var(--eh-amber);
  border-radius: 8px;
  color: var(--eh-teal-ink);
  font-size: 13px;
}
.dir-orec-detail .dir-conflict-warning {
  margin: 12px 0;
}
.dir-code-name {
  font-size: 14.5px;
  font-weight: 600;
  color: var(--eh-teal-ink);
}
.dir-code-desc {
  font-size: 13px;
  color: var(--eh-muted);
  margin-top: 3px;
  line-height: 1.45;
}

/* Key/value rows (attestation, dates) */
.dir-kv {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.dir-kv-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 13px;
}
.dir-kv-row span {
  color: var(--eh-muted);
}
.dir-kv-row strong {
  color: var(--eh-teal-ink);
  font-weight: 600;
  text-align: right;
}
.dir-soft-warning.sm {
  margin-top: 12px;
  padding: 10px 12px;
  font-size: 12px;
}

/* Audit timeline */
.dir-timeline {
  margin-top: 6px;
}
.dir-tl-item {
  display: flex;
  gap: 13px;
}
.dir-tl-rail {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: none;
}
.dir-tl-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-top: 4px;
  flex: none;
}
.dir-tl-line {
  width: 2px;
  flex: 1;
  min-height: 14px;
  background: var(--eh-divider);
}
.dir-tl-item:last-child .dir-tl-line {
  display: none;
}
.dir-tl-body {
  padding-bottom: 14px;
  min-width: 0;
}
.dir-tl-head {
  font-size: 13px;
}
.dir-tl-action {
  font-weight: 600;
  color: var(--eh-teal-ink);
}
.dir-tl-when {
  color: var(--eh-muted);
}
.dir-tl-who {
  font-size: 12px;
  color: var(--eh-muted);
  margin-top: 1px;
}
.dir-tl-note {
  font-size: 12.5px;
  color: var(--eh-muted);
  font-style: italic;
  margin-top: 4px;
}

/* Actions */
.dir-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 18px;
}
/* Next-step strip: the one sentence that orders everything below it. */
.dir-next-step {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 8px;
  background: var(--eh-teal-wash);
  border: 1px solid var(--eh-teal);
  color: var(--eh-teal-ink);
  font-size: 13.5px;
  font-weight: 600;
}
.dir-next-step .glyphicon {
  color: var(--eh-teal);
  margin-top: 1px;
}
/* Open review flag banner (amber = attention, not danger). */
.dir-review-flag {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 10px;
  padding: 10px 14px;
  border-radius: 8px;
  background: color-mix(in srgb, var(--eh-amber) 18%, var(--eh-white));
  border: 1px solid var(--eh-amber);
  color: var(--eh-teal-ink);
  font-size: 13px;
}
.dir-action-group {
  margin-top: 14px;
}
.dir-action-group-label {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: var(--eh-muted);
  margin-bottom: 2px;
}
.dir-action-group .dir-actions {
  margin-top: 8px;
}
.dir-last-reviewed {
  display: block;
  margin-top: 6px;
  font-size: 12px;
  color: var(--eh-muted);
}
.dir-row-waiting {
  font-size: 12.5px;
  color: var(--eh-muted);
  align-self: center;
}
.dir-btn-primary,
.dir-btn-secondary,
.dir-btn-warning {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 16px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
}
.dir-btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.dir-btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.dir-btn-warning:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.dir-btn-danger {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 16px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
}

/* Note box */
.dir-note-box {
  margin-top: 12px;
  padding: 14px;
  background: color-mix(in srgb, var(--eh-amber) 18%, var(--eh-white));
  border: 1px solid var(--eh-amber);
  border-radius: 8px;
}
.dir-note-box label {
  display: block;
  font-weight: 600;
  font-size: 13px;
  color: var(--eh-teal-ink);
  margin-bottom: 6px;
}
.dir-input {
  width: 100%;
  padding: 9px 12px;
  border-radius: 8px;
  font-size: 14px;
}
.dir-note-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 10px;
}

/* Soft warning */
.dir-soft-warning {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 14px;
  padding: 12px 14px;
  background: color-mix(in srgb, var(--eh-amber) 18%, var(--eh-white));
  border: 1px solid var(--eh-amber);
  border-radius: 8px;
  color: var(--eh-teal-ink);
  font-size: 13px;
}
/* Info variant — calmer reminder (re-auth due soon, not overdue) */
.dir-soft-warning.info {
  background: var(--eh-teal-wash);
  border-color: var(--eh-teal);
  color: var(--eh-teal);
}

/* Document history modal */
.dir-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.7);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
  padding: 20px;
}
.dir-modal {
  background: var(--eh-white);
  border-radius: 12px;
  width: 100%;
  max-width: 560px;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.3);
}
.dir-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
  border-bottom: 1px solid var(--eh-divider);
}
/* Patient identity line pinned in overlay headers (name · MRN · DOB). */
.dir-modal-patient {
  margin-top: 4px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--eh-muted);
}
.dir-modal-header h3 {
  margin: 0;
  font-size: 18px;
  color: var(--eh-teal-ink);
  display: flex;
  align-items: center;
  gap: 10px;
}
.dir-close-btn {
  border: none;
  background: transparent;
  font-size: 16px;
  color: var(--eh-muted);
  cursor: pointer;
  /* Real tap target on a phone; the negative margin keeps the header height. */
  width: 44px;
  height: 44px;
  margin: -10px -12px -10px 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.dir-info-modal {
  max-width: 640px;
}
/* Actions lead the modal, separated from the read-only detail below them. */
/* The action row and the document links are separated by a rule, so each needs
   room on its own side of it — 4px under the divider read as the links being
   attached to the buttons above rather than being their own group. */
.dir-info-actions {
  padding-bottom: 18px;
  margin-bottom: 0;
  border-bottom: 1px solid var(--eh-divider);
}
.dir-info-doclinks {
  margin-top: 18px;
  margin-bottom: 20px;
}
.dir-modal-header h3 .dir-badge {
  margin-left: 2px;
}
.dir-info-modal .dir-modal-body h4 {
  margin: 18px 0 8px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--eh-muted);
}
.dir-info-modal .dir-detail-grid h4 {
  margin-top: 0;
}
.dir-modal-body {
  padding: 20px 24px;
  overflow-y: auto;
}
.dir-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid var(--eh-divider);
}

/* Slide transition */
.dir-slide-enter-active,
.dir-slide-leave-active {
  transition: all 0.25s ease;
  overflow: hidden;
}
.dir-slide-enter-from,
.dir-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

@keyframes dir-spin {
  to {
    transform: rotate(360deg);
  }
}


.dir-utilbar {
  border-bottom: 1px solid var(--eh-divider);
  background: var(--eh-white);
}
.dir-utilbar-inner {
  max-width: 1080px;
  margin: 0 auto;
  padding: 14px var(--dir-gutter);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.dir-back-chev {
  font-size: 16px;
  line-height: 0;
}
.dir-add-btn.ghost {
  background: var(--eh-white);
  color: var(--eh-teal);
  border: 1px solid var(--eh-teal);
}
.dir-add-btn.ghost:hover {
  background: var(--eh-teal-wash);
}
.dir-main {
  max-width: 1080px;
  margin: 0 auto;
  padding: 24px var(--dir-gutter) 72px;
}
.dir-main .dir-empty,
.dir-page > .dir-empty {
  max-width: 560px;
  margin: 48px auto;
}

/* patient banner */
.dir-banner {
  background: var(--eh-white);
  border: 1px solid var(--eh-divider);
  border-radius: 10px;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 1px 2px rgba(26, 54, 93, 0.04);
}
.dir-avatar {
  width: 46px;
  height: 46px;
  border-radius: 50%;
  flex: none;
  background: var(--eh-teal-wash);
  border: 1px solid var(--eh-teal);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  font-weight: 600;
  color: var(--eh-teal);
  letter-spacing: 0.5px;
}
.dir-banner-body {
  min-width: 0;
  flex: 1;
}
.dir-banner-eyebrow {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: var(--eh-muted);
  margin-bottom: 3px;
}
.dir-banner-name {
  margin: 0;
  font-size: 23px;
  font-weight: 700;
  letter-spacing: -0.5px;
  color: var(--eh-teal-ink);
  line-height: 1.05;
}
/* Placeholder identity is muted so it never reads as a resolved patient name —
   the whole point is that this is not yet anybody. */
.dir-banner-name.is-loading {
  color: var(--eh-muted);
  font-weight: 600;
}
.dir-avatar.is-loading {
  color: var(--eh-muted-border);
  background: var(--eh-muted-fill);
}
.dir-banner-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 16px;
  margin-top: 7px;
}
.dir-banner-meta-loading {
  font-size: 13px;
  color: var(--eh-muted);
}
.dir-banner-strong {
  font-size: 14px;
  font-weight: 700;
  color: var(--eh-teal-ink);
}
.dir-dotsep {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: var(--eh-muted-border);
  align-self: center;
}
.dir-banner-mrn {
  font-size: 13px;
  color: var(--eh-muted);
  font-family: var(--eh-font-data);
}

/* ===== Submissions card (active hero on top + in-flight rows) ===== */
.dir-submissions {
  /* First card under the patient banner. */
  margin-top: 26px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
/* Status badge leads on its own line; below it one horizontal band (chip +
   title left, actions right). Generous symmetric padding keeps the buttons
   clear of the separator lines; the band wraps on narrow screens. No border
   on the first row — the "Submissions" label row above already draws the
   section divider. */
.dir-submission-row {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  padding: 16px 0;
  border-top: 1px solid var(--eh-divider);
}
.dir-submission-row:first-of-type {
  border-top: 0;
  padding-top: 4px;
}
/* Per-submission document affordances, mirroring the hero's doc row. */
.dir-submission-doc-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px 14px;
  margin-top: 10px;
}
.dir-submission-doc-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 12.5px;
  padding: 6px 12px;
}
.dir-submission-row:last-of-type {
  padding-bottom: 4px;
}
.dir-submission-status {
  flex: none;
}
.dir-submission-band {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px 14px;
  width: 100%;
}
.dir-submission-main {
  flex: 1 1 240px;
  min-width: 0;
}
.dir-submission-actions {
  margin-top: 0;
  margin-left: auto;
}
/* The legend "?" rides in this row, so the label must not consume the width.
   The row sits mid-card, under the active or Full Code hero, so it owns
   the divider separating the hero from the submissions section. */
.dir-widget-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border-top: 1px solid var(--eh-divider);
  padding-top: 14px;
}
.dir-widget {
  background: var(--eh-white);
  border: 1px solid var(--eh-divider);
  border-radius: 10px;
  padding: 14px 18px;
  box-shadow: 0 1px 2px rgba(26, 54, 93, 0.04);
}
.dir-widget-label {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: var(--eh-muted);
  margin-bottom: 10px;
}
.dir-widget-form {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.dir-file-chip {
  width: 38px;
  height: 46px;
  border-radius: 6px;
  flex: none;
  background: var(--eh-muted-fill);
  border: 1px solid var(--eh-divider);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  color: var(--eh-muted);
  font-family: var(--eh-font-data);
}
.dir-widget-form-body {
  min-width: 0;
}
.dir-widget-filename {
  font-size: 13px;
  font-weight: 600;
  font-family: var(--eh-font-data);
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
}
.dir-widget-sub {
  font-size: 12px;
  color: var(--eh-muted);
  margin: 3px 0 7px;
}
/* Status badge rendered as a <button>: keep the pill look, add the affordance. */
.dir-badge-btn {
  border: 0;
  font-family: inherit;
  cursor: pointer;
}
.dir-badge-btn:hover,
.dir-badge-btn:focus-visible {
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--eh-teal) 35%, transparent);
}
.dir-widget-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}
/* Widget-scale buttons: same components as the modal actions, one notch smaller. */
.dir-widget-actions .dir-btn-primary,
.dir-widget-actions .dir-btn-secondary {
  padding: 7px 14px;
  font-size: 12.5px;
}
.dir-widget-empty {
  font-size: 13px;
  color: var(--eh-muted);
  padding: 4px 0;
}

/* section header */
/* active hero — embedded in the Submissions card, so the widget owns the
   frame and horizontal padding: the hero has no background/border of its own
   (a nested card read as two frames), and its head/body run edge to edge. */
.dir-hero-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.dir-hero-head {
  padding: 0 0 12px;
  border-bottom: 1px solid var(--eh-divider);
}
.dir-hero-head-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.dir-badge.lg {
  font-size: 12px;
  padding: 5px 12px 5px 10px;
}
.dir-legend-q {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid var(--eh-muted-border);
  background: var(--eh-white);
  color: var(--eh-muted);
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}
.dir-legend-q:hover {
  background: var(--eh-teal-wash);
  color: var(--eh-teal);
  border-color: var(--eh-teal);
}
.dir-hero-spacer {
  flex: 1 1 auto;
}
/* The one entry point to everything that is context rather than the directives
   themselves. 44px so it is a real tap target on a phone. */
/* Icon + "Details" label, so the pill reads as an action instead of leaving the
   user to guess what a bare (i) opens. Still 44px tall for a phone tap target. */
.dir-hero-info-btn {
  min-height: 44px;
  flex: none;
  margin: -6px -6px -6px 0;
  padding: 0 14px;
  border: 0;
  border-radius: 22px;
  background: transparent;
  color: var(--eh-teal);
  font-size: 19px;
  line-height: 1;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.dir-hero-info-text {
  font-size: 14px;
  font-weight: 600;
}
.dir-hero-info-btn:hover,
.dir-hero-info-btn:focus-visible {
  background: var(--eh-teal-wash);
  color: var(--eh-teal);
}
/* Owns the spacing between the document button and the "view alongside" link;
   they sit on one line when there is room and stack when there isn't. */
/* Footer band under the codes list. No divider of its own — the Submissions
   label row below already draws the section separator. */
.dir-hero-doc-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 18px;
  margin-top: 14px;
}
/* Same component and scale as the submission rows' "More actions" button —
   the widget-level secondary, one notch smaller than modal actions. */
.dir-hero-doc-btn {
  display: inline-flex;
  justify-content: center;
  padding: 7px 14px;
  font-size: 12.5px;
}
/* subtle side-by-side review entry beside the hero doc button */
.dir-hero-alongside {
  font-size: 12.5px;
}
.dir-hero-body {
  padding: 16px 0 4px;
}
.dir-hero-codes {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 320px;
  overflow-y: auto;
  padding-right: 8px;
}
.dir-hero-code {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 12px 14px;
  border: 1px solid var(--eh-divider);
  border-left: 3px solid var(--eh-copper);
  border-radius: 8px;
  background: var(--eh-muted-fill);
  position: relative;
}
.dir-hero-code.affirming {
  border-left-color: var(--eh-clinical);
}
.dir-hero-code-icon {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 24px;
  padding: 0 6px;
  border-radius: 6px;
  background: var(--eh-copper-wash);
  color: var(--eh-copper);
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}
.dir-hero-code.affirming .dir-hero-code-icon {
  background: color-mix(in srgb, var(--eh-clinical) 15%, var(--eh-white));
  color: var(--eh-clinical);
}
.dir-hero-code-body {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 2px 8px;
}
.dir-hero-code-name {
  font-size: 14.5px;
  font-weight: 700;
  color: var(--eh-teal-ink);
  letter-spacing: -0.2px;
}
.dir-hero-code-desc {
  font-size: 12.5px;
  color: var(--eh-muted);
  line-height: 1.35;
  flex: 1 1 100%;
}

/* approver block — now rendered inside the record-info modal */
.dir-info-approver {
  display: flex;
  align-items: center;
  gap: 11px;
  min-width: 0;
  padding: 11px 14px;
  background: var(--eh-muted-fill);
  border: 1px solid var(--eh-divider);
  border-radius: 10px;
  margin-bottom: 14px;
}
.dir-approver {
  display: flex;
  align-items: center;
  gap: 11px;
  min-width: 0;
}
.dir-approver-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  flex: none;
  background: var(--eh-white);
  border: 1px solid var(--eh-divider);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  color: var(--eh-teal);
}
.dir-approver-line {
  font-size: 13.5px;
  color: var(--eh-teal-ink);
}
.dir-muted-inline {
  color: var(--eh-muted);
}
.dir-approver-npi {
  font-size: 12px;
  color: var(--eh-muted);
  font-family: var(--eh-font-data);
}
.dir-expired-banner {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 12px 0 0;
  padding: 12px 14px;
  border-radius: 6px;
  background: var(--eh-copper-wash);
  border: 1px solid var(--eh-copper);
  border-left: 4px solid var(--eh-copper-deep);
  color: var(--eh-copper-deep);
  font-size: 0.95rem;
  line-height: 1.45;
}
.dir-reauth-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 13px;
  border-radius: 8px;
  font-size: 12.5px;
  font-weight: 600;
  background: var(--eh-muted-fill);
  color: var(--eh-muted);
}
.dir-reauth-pill.soon {
  background: color-mix(in srgb, var(--eh-amber) 18%, var(--eh-white));
  color: var(--eh-teal-ink);
}
.dir-reauth-pill.overdue {
  background: var(--eh-copper-wash);
  color: var(--eh-copper-deep);
}
.dir-reauth-icon {
  font-size: 13px;
  line-height: 0;
}

/* Full Code hero: reason line under the tile — quiet, never competing with
   the status (same stance as the list page's "because" line). */
.dir-fullcode-because {
  margin-top: 10px;
  font-size: 12.5px;
  color: var(--eh-muted);
}

/* previous / inactive */
.dir-other {
  margin-top: 26px;
  background: var(--eh-white);
  border: 1px solid var(--eh-divider);
  border-radius: 10px;
  overflow: hidden;
}
.dir-other-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 22px;
  cursor: pointer;
  user-select: none;
}
.dir-other-head:hover {
  background: var(--eh-muted-fill);
}
.dir-other-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--eh-muted);
}
.dir-other-count {
  font-size: 12px;
  font-weight: 700;
  color: var(--eh-muted);
  background: var(--eh-muted-fill);
  padding: 2px 9px;
  border-radius: 999px;
}
.dir-other-spacer {
  flex: 1;
}
.dir-other-summary {
  font-size: 12.5px;
  color: var(--eh-muted);
}
.dir-other-chev,
.dir-orec-chev {
  color: var(--eh-muted);
  font-size: 15px;
  transition: transform 0.18s;
}
.dir-other-chev.open,
.dir-orec-chev.open {
  transform: rotate(180deg);
}
.dir-other-body {
  border-top: 1px solid var(--eh-divider);
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.dir-orec {
  border: 1px solid var(--eh-divider);
  border-radius: 9px;
  overflow: hidden;
  background: var(--eh-white);
}
.dir-orec-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 13px 16px;
  cursor: pointer;
}
.dir-orec-row:hover {
  background: var(--eh-muted-fill);
}
.dir-orec-main {
  flex: 1;
  min-width: 0;
}
.dir-orec-form {
  font-size: 13.5px;
  font-weight: 600;
  color: var(--eh-teal-ink);
}
.dir-orec-sub {
  font-size: 12px;
  color: var(--eh-muted);
  margin-top: 2px;
}
.dir-orec-date {
  text-align: right;
  flex: none;
}
.dir-orec-date small {
  display: block;
  font-size: 11px;
  color: var(--eh-muted);
}
.dir-orec-date strong {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--eh-muted);
}
.dir-orec-detail {
  border-top: 1px solid var(--eh-divider);
  padding: 16px;
  background: var(--eh-muted-fill);
}
.dir-orec-doclinks {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 18px;
  margin-top: 10px;
}
/* The one way into a previous/inactive record's detail and actions. */
.dir-orec-info {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  min-height: 44px;
  padding: 0 16px;
  border: 1px solid var(--eh-muted-border);
  border-radius: 8px;
  background: var(--eh-white);
  color: var(--eh-teal);
  font-size: 13.5px;
  font-weight: 700;
  cursor: pointer;
}
.dir-orec-info:hover {
  background: var(--eh-teal-wash);
  border-color: var(--eh-teal);
}

/* legend modal */
.dir-legend-modal .dir-modal-body {
  display: flex;
  flex-direction: column;
}
.dir-legend-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid var(--eh-divider);
}
.dir-legend-desc {
  font-size: 13px;
  color: var(--eh-muted);
  line-height: 1.5;
  padding-top: 4px;
}

/* thin scrollbar for the codes list */
.dd-scroll {
  scrollbar-color: var(--eh-muted) var(--eh-muted-fill);
}
.dd-scroll::-webkit-scrollbar {
  width: 10px;
}
.dd-scroll::-webkit-scrollbar-track {
  background: var(--eh-muted-fill);
  border-radius: 8px;
}
.dd-scroll::-webkit-scrollbar-thumb {
  background: var(--eh-muted);
  border-radius: 8px;
  border: 3px solid var(--eh-muted-fill);
}
/* ===== Phone layout. Kept LAST in the file on purpose: these rules have
   the same specificity as the base rules above, so anything declared after
   them would silently win and the mobile layout would not apply at all. ===== */

@media (max-width: 700px) {
  .dir-detail-grid {
    grid-template-columns: 1fr;
  }
  .dir-page {
    --dir-gutter: 16px;
  }
  .dir-utilbar-inner {
    padding: 12px var(--dir-gutter);
  }
  .dir-main {
    padding: 16px var(--dir-gutter) 56px;
  }
  /* Tighter patient banner on phones */
  .dir-banner {
    padding: 12px 14px;
    gap: 12px;
  }
  .dir-avatar {
    width: 40px;
    height: 40px;
    font-size: 15px;
  }
  .dir-banner-name {
    font-size: 20px;
  }
  .dir-banner-meta {
    margin-top: 5px;
    gap: 3px 12px;
  }
  .dir-banner-strong {
    font-size: 13px;
  }
  /* Denser active hero */
  .dir-hero-head {
    padding: 0 0 10px;
  }
  .dir-hero-doc-btn {
    width: 100%;
    height: 40px;
    margin-top: 10px;
  }
  .dir-hero-body {
    padding: 12px 0 4px;
  }
  .dir-hero-code {
    gap: 9px;
    padding: 10px 12px;
  }
  .dir-hero-code-icon {
    min-width: 36px;
    height: 22px;
    font-size: 10px;
  }
  .dir-hero-code-name {
    font-size: 14px;
  }
  /* Actions go full-width and stack: side-by-side buttons at 390px left each
     one too narrow to read and under the 44px tap target. */
  .dir-actions {
    flex-direction: column;
    align-items: stretch;
  }
  .dir-actions > button {
    width: 100%;
    min-height: 44px;
  }
  .dir-widget {
    padding: 12px 14px;
    min-width: 0;
  }
  /* Widget actions stack full-width for the same reason as .dir-actions:
     side-by-side at 390px is too narrow and under the 44px tap target. */
  .dir-widget-actions {
    flex-direction: column;
    align-items: stretch;
  }
  .dir-widget-actions > button {
    width: 100%;
    min-height: 44px;
    justify-content: center;
  }
  /* Long generated filenames must wrap rather than push the page wider. */
  .dir-widget-form-body,
  .dir-widget-filename {
    min-width: 0;
    max-width: 100%;
    overflow-wrap: anywhere;
    text-align: left;
  }
  /* Utility bar: back link and the add button stack full-width rather than
     squeezing into two half-width columns of wrapped text. */
  .dir-utilbar-inner {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
  .dir-utilbar-inner > button {
    width: 100%;
    min-height: 44px;
    justify-content: center;
  }
  /* Remaining sub-44px controls inside the modal: the document link and the
     footer's Close. */
  .dir-info-doclinks .dir-doc-link {
    min-height: 44px;
  }
  .dir-modal-footer .dir-btn-primary {
    min-height: 44px;
  }
  /* The status badge alongside the title pushed "Record details" onto two
     lines; let them sit as one wrapping row instead. */
  .dir-modal-header h3 {
    flex-wrap: wrap;
    gap: 6px;
    font-size: 17px;
  }
  /* Modals (record info, legend) go full-screen on a phone, minus the app's
     fixed Messages bar. That bar is pinned at z-index 2147483647 (max int), so
     no overlay can ever stack above it — the only way to keep the footer's
     Close button tappable is to reserve its 48px. */
  .dir-modal-overlay {
    padding: 0 0 48px;
  }
  .dir-modal {
    width: 100%;
    max-width: none;
    max-height: calc(100dvh - 48px);
    height: 100%;
    border-radius: 0;
  }
  /* Controls a finger has to hit. */
  .dir-legend-q {
    width: 44px;
    height: 44px;
    font-size: 15px;
    margin: -8px 0;
  }
  /* Padding, not flex: the filename relies on text-overflow: ellipsis, which
     only applies to a block/inline-block box. */
  .dir-widget-filename {
    min-height: 44px;
    padding: 12px 0;
  }
  /* A lone separator dot on its own wrapped line reads as a typo. */
  .dir-dotsep {
    display: none;
  }
  /* Previous/inactive summary row: four columns in a single flex line collided
     at phone width — the form label overlapped the date and clipped. Stack it. */
  .dir-orec-row {
    flex-wrap: wrap;
    gap: 6px 10px;
    padding: 12px 14px;
  }
  .dir-orec-main {
    flex: 1 0 100%;
    order: 2;
  }
  .dir-orec-date {
    text-align: left;
    order: 3;
    flex: 1 1 auto;
  }
  .dir-orec-date small {
    display: inline;
  }
  .dir-orec-date strong {
    margin-left: 4px;
  }
  .dir-orec-chev {
    order: 4;
    margin-left: auto;
  }
}

</style>
